// IMPORT REACT
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { format, isValid } from "date-fns";
import dayjs, { Dayjs } from "dayjs";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// IMPORT CSS
import "../../../theme/style.scss";
import "react-phone-number-input/style.css";

// IMPORT UI COMPONENTS
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  Select,
  MenuItem,
  Dialog,
  AppBar,
  Toolbar,
  Divider,
  DialogContent,
  useMediaQuery,
  useTheme,
  Checkbox,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Paper,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import PhoneInput from "react-phone-number-input";
import DatePickerComponent from "../../../components/CustomFields/DatePicker";
import CustomBreadcrumb from "../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import {
  CustomAsyncSelect,
  CustomSelect,
} from "../../../components/CustomFields/CustomSelect";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomTextField } from "../../../components/CustomFields/TextField";
import {
  createInputPropsForNumberField,
  createInputPropsWithSeparator,
  underModules,
} from "../../../shared/data";
import { ExpandLess, ExpandMore, Fullscreen, Edit } from "@mui/icons-material";

// IMPORT SERVICE
import { AlertType } from "../../../services/models/AlertType";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import {
  GetAppointmentDetail,
  CreateAppointment,
  UpdateAppointment,
  GetAppointmentNumber,
  GetAppointmentAvailabilityList,
  GetAppointmentAvailabilityDateList,
  UpdateAppointmentStatus,
} from "../../../services/AppointmentService";
import { GetMedicineCompanyList } from "../../../services/MedicineCompanyService";
import {
  GetPatientDetail,
  GetPatientList,
} from "../../../services/PatientService";
import { GetDoctorList } from "../../../services/DoctorService";
import {
  GetBranchLanguageList,
  GetLanguageListForSelect,
} from "../../../services/LanguageService";
import { GetCountryListForSelect } from "../../../services/CountryService";
import { GetCountryRegionListForSelect } from "../../../services/CountryRegionService";
import { PreferencesContext } from "../../../PreferenceContext";
import {
  GetMedicalRepresentativeDetail,
  GetMedicalRepresentativeList,
} from "../../../services/MedicalRepresentativeService";
import { GetDepartmentList } from "../../../services/DepartmentService";
import { PAGE_SIZE } from "../../../services/common/const";
import { GetSpecialityList } from "../../../services/SpecialityService";
import {
  BPMesurementDefaultOptions,
  genderOptions,
  maritalStatusOptions,
  prefixOptions,
  ReferredByOptions,
  SpO2MesurementDefaultOptions,
  temperatureByPalpationOptions,
  temperatureOptions,
  // temperatureOptions,
  // referredByOptions,
  visitTypeOptions,
  visitTypeOptionsMedicalRepresentative,
} from "../../../shared/Data/FrontDesk/Appointment";
import {
  getAge,
  handleFocus,
  lastOpdVisitTime,
} from "../../../shared/CommonFunction";
import CustomDateTimePicker from "../../../components/TimePicker/CustomDateTimePicker";
import PatientSave from "../../Patient/PatientMaster/PatientMasterSave";
import MedicalRepresentativeSave from "../../MedicalRepresentative/MedicalRepresentativeMaster/MedicalRepresentativeSave";
import DoctorSave from "../../Doctor/Doctor/DoctorSave";
import { CustomSwitch } from "../../../components/CustomFields/CustomSwitch";
import CustomTooltip from "../../../components/ToolTip/CustomToolTip";
import { GetItemList } from "../../../services/ItemService";
import ItemSave from "../../Item/ItemSave";
import { components } from "react-select";
import { GetLocationList } from "../../../services/LocationService";
import CustomDatePicker from "../../../components/TimePicker/CustomDatePicker";
import {
  GetLocationtoTeamMemberList,
  GetTeamMemberList,
  GetTeamMemberLocationList,
} from "../../../services/TeamMemberService";
import { GetAppointmentReportParameterList, GetInvestigationParameterRangeList } from "../../../services/ReportParameterService";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

// SCHEMA FOR OPTION
interface selectOption {
  label: string;
  value: string;
}

// SCHEMA FOR REASONS OF APPOINTMENT
interface IReasonForVisit {
  displayOrder: number;
  itemId: selectOption | null;
  itemName: string;
}

interface IReportParameter {
  appointmentReportParameterId?: number;
  appointmentId?: number;
  consumerId?: number;
  orgId?: number;
  branchId?: number;
  departmentId?: number;
  specialityId?: number;
  opdId?: number;
  patientId?: number;
  investigationId?: number;
  investigationName?: string;
  investigationParameterId?: number;
  parameterName?: string;
  investigationParameterUnitId?: number;
  unitName?: string;
  unitValue?: string;
  specimen?: string;
  remarks?: string;
  optionValue?: string; 
  displayOrder?: number;
  isDeleted?: boolean;
}

// SCHEMA FOR FORM
interface formInitialValueTypes {
  appointmentId: number;
  underModule: selectOption | null;
  departmentId: selectOption | null;
  departmentName: string | null;
  specialityId: selectOption | null;
  specialityName: string | null;
  appointmentTypeCode: string;
  appointmentNumber: number;
  patientId: selectOption | null;
  patientName: string | null;
  medicalRepresentativeId: selectOption | null;
  medicalRepresentativeName: string | null;
  companyNameId: selectOption | null;
  companyName: string | null;
  guestId: number;
  consultingDoctorId: selectOption | null;
  consultingDoctorName: string | null;
  referredByCode: selectOption | null;
  referredByCodeName: selectOption | null;
  referredByName: string;
  referralPatientId: selectOption | null;
  referralPatientName: string | null;
  referralDoctorId: selectOption | null;
  referralDoctorName: string | null;
  visitTypeCode: selectOption | null;
  visitTypeCodeName: string | null;
  prefix: selectOption | null;
  prefixName: string | null;
  emrId: string | null;
  status: string;
  isEmergency: boolean;
  weight: number | null;
  height: number | null;
  pr: number | null;
  spO2Amount: number | null;
  spO2SiteUnit: string;
  bpLeftAmount: number | null;
  bpRightAmount: number | null;
  bpDisplayValue: string | null;
  bpSiteUnit: string;
  temperatureAmount: number | null;
  temperatureUnit: string;
  temperatureByPalpation: selectOption | null;
  respiratoryRate: number | null;
  firstName: string;
  middleName: string;
  lastName: string;
  patientFirstName: string;
  patientLastName: string;
  patientMiddleName: string;
  dateOfBirth: string;
  ageYear: number;
  ageMonth: number;
  ageDay: number;
  preferredLanguage: selectOption | null;
  preferredLanguageName: string | null;
  maritalStatus: selectOption | null;
  maritalStatusName: string | null;
  gender: selectOption | null;
  genderName: string | null;
  location: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  postalCode: string | null;
  countryId: selectOption | null;
  countryName: string | null;
  countryRegionId: selectOption | null;
  countryRegionName: string | null;
  email: string;
  primaryMobileNumber: string;
  secondaryMobileNumber: string;
  rbs: number | null;
  headCircumference: number | null;
  motherHeight: number | null;
  fatherHeight: number | null;
  note: string;
  appointmentDate: Date | null;
  appointmentTime: string | null;
  reasonForVisitColl: Array<IReasonForVisit>;
  reportParameterColl: Array<IReportParameter>
  slotNumber: number;
  financialYear: string;
  isDeleted: boolean;
  isOutReach: boolean;
  locationId: selectOption | null;
  locationName: string | null;
  locationTypeCode: string;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  consumerId: string;
  orgId: string;
  branchId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
  mode: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  consumerId: "",
  orgId: "",
  branchId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
  mode: "DROPDOWN",
};

type Order = "asc" | "desc" | "";

// SCHEMA FOR TABLE
interface EnhancedTableProps {
  numSelected: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: String;
  rowCount: number;
}
// SCHEMA FOR RESULTDATA
interface ResultData {
  appointmentReportParameterColl: Array<any>;
  totalCount: number;
}
// DEFAULT VALUES FOR RESULT DATA
const initResultData: ResultData = {
  appointmentReportParameterColl: [],
  totalCount: 0,
};
// SCHEMA
interface Data {
  reportParameterId: number;
  consumerId: number;
  orgId: number;
  branchId: number;
  isDeleted: boolean;
}
// TABLE ROWS FOR SKELETON
const skeletonRowCount = 5;

export default function AppointmentSave(Props: any) {
  // PROPS
  const {
    isForPopUp = false,
    handleClosePopUp,
    opd,
    isCalendar = false,
    calendarDate,
    tabType: propTabType = "",
    prefilledAppointmentData,
  } = Props;

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const theme = useTheme();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  const location = useLocation();
  const isWalkIn = location?.state?.isWalkIn ?? false;
  const prefilledPatient = location?.state?.prefilledPatient ?? null;

  // GETTTING PARAMS

  let urlParams = useQuery();
  let curRawAppointmentId = urlParams.get("q");
  let curAppointmentId = 0;

  let currentTab = urlParams.get("t");
  let page = urlParams.get("page");
  const qParam = urlParams.get("rpPage");
  let encodedQuery = qParam;
  let currTabType = 0;
  let query = qParam || "";
  let curPage = 1;

  if (
    page != null &&
    page != "" &&
    page != undefined &&
    encodedQuery != null &&
    encodedQuery != undefined
  ) {
    curPage = +page;
    // Encoded Query
    encodedQuery = encodeURIComponent(encodedQuery);
    // Decode Query
    query = decodeURIComponent(encodedQuery);
  } else {
  }

  if (!isForPopUp) {
    if (curRawAppointmentId !== null && curRawAppointmentId !== undefined) {
      let decId = edService.decrypt(curRawAppointmentId);
      if (decId !== "" && decId !== null && decId !== undefined) {
        curAppointmentId = +decId;
      }
    } else {
      if (isForPopUp && opd?.appointmentId > 0) {
        curAppointmentId = opd.appointmentId;
      }
    }
    if (currentTab === "report-parameter") {
      currTabType = 1;
    } else {
      currTabType = 0; // default to basic-information
    }
  } else {
    if (isForPopUp && opd?.appointmentId > 0 && !prefilledAppointmentData) {
      curAppointmentId = opd.appointmentId;
    }
  }

  let curRawTab = urlParams.get("tab");
  let curTabType = "";
  if (curRawTab !== null && curRawTab !== undefined) {
    let decTab = edService.decrypt(curRawTab);
    if (decTab !== "" && decTab !== null && decTab !== undefined) {
      curTabType = decTab; // decrypted tab value
    }
  }

  // If a tab type is provided via props (e.g. from OPDClinical), prefer that
  if (propTabType) {
    curTabType = propTabType.toString().toLowerCase().replace("_", "-");
  }

  let isEditDisabled = false;
  const disabledTabs = ["in-consultation", "complete"]; // <-- define your two tab names/types

  if (curTabType > "" && disabledTabs.includes(curTabType?.toLowerCase())) {
    isEditDisabled = true;
  }

  // IF NOT FIND THEN NAVIGATE TO NOT FOUND PAGE  --- PENDING -- ASK TO SIR ABOUT THIS

  // BREAK POINTS
  const smd = useMediaQuery(theme.breakpoints.down("sm"));
  const lgd = useMediaQuery(theme.breakpoints.down("lg")); //-- FOR REF PLEASE ONLY TAKE THEME BREAK POINTS FOR MEDIA QUERY
  // const md = useMediaQuery(theme.breakpoints.up("md"));
  // const lg = useMediaQuery(theme.breakpoints.up("lg"));

  //INPUT PROPS
  const weightProps = createInputPropsForNumberField(3, 3, true);
  const heightProps = createInputPropsForNumberField(3, 1, true);
  const headCircumferenceProps = createInputPropsForNumberField(3, 1, true);
  const prProps = createInputPropsForNumberField(3, 0, false);
  const rrProps = createInputPropsForNumberField(3, 0, false);
  const spo2Props = createInputPropsForNumberField(3, 0, false);
  const tempetureProps = createInputPropsForNumberField(3, 1, true);
  const bpProps = createInputPropsWithSeparator(3, 3, "/", 300);

  // STATES FOR DATA STORAGE  -- PLEASE USE PROPER TYPES FOR STATE
  const [param, setParam] = useState(parameters);
  const [departmentColl, setDepartmentColl] = useState<any[]>([]);
  const [specialityColl, setSpecialityColl] = useState<any[]>([]);
  const [countryRegionData, setCountryRegionData] = useState<any[]>([]);
  const [countryData, setCountryData] = useState<any[]>([]);
  const [patientColl, setPatientColl] = useState<any[]>([]);
  const [refDoctorColl, setRefDoctorColl] = useState<any[]>([]);
  const [consultingDoctorColl, setConsultingDoctorColl] = useState<any[]>([]);
  const [itemColl, setItemColl] = useState<any[]>([]);
  const [mrColl, setMrColl] = useState<any[]>([]);
  const [languageData, setLanguageData] = useState([]);
  const [medicineCompanyColl, setMedicineCompanyColl] = useState<any[]>([]);
  const [deletedReasonForVisit, setDeletedReasonForVisit] = useState<any[]>([]);
  const [seeMore, setSeeMore] = useState<boolean>(false);
  const [seeMoreVitals, setSeeMoreVitals] = useState<boolean>(
    isWalkIn ? true : false,
  );
  const [lastOpdCaseDate, setLastOpdCaseDate] = useState("");
  const [lastOpdDateDiffrence, setLastOpdDateDiffrence] = useState({
    Year: 0,
    Month: 0,
    Day: 0,
  });

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [customPopUp, setCustomPopUp] = React.useState({
    open: false,
    name: "",
    code: "",
  });
  const [locationData, setLocationData] = useState<any[]>([]);
  const [appointmentTimeSlots, setAppointmentTimeSlots] = useState([]);
  const [appointmentDate, setAppointmentDate] = useState([]);
  const [appointmentTime, setAppointmentTime] = useState([]);
  const [openDeleteDilog, setOpenDeleteDilog] = useState(false);
  const [languageResultData, setLanguageResultData] = useState<any[]>([]);

  const [resultData, setResultData] = useState<ResultData>(initResultData);
  const [order, setOrder] = React.useState<Order>(""); // DON"T USE  DIFFERENT STATE FOR EVERY PARAMETER USE PARAMETER STATE INSTAND
  const [orderBy, setOrderBy] = React.useState<keyof Data>("none");
  const [selected, setSelected] = useState<readonly number[]>([]);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);
  const [unitColl, setUnitColl] = useState<any[]>([]);
  const [value, setValue] = useState(query || "");
  const [investigationParameterRangeColl, setInvestigationParameterRangeColl] =
  useState<any[]>([]);

  // TABLE HEADER
  function EnhancedTableHead(props: EnhancedTableProps) {
    return (
      <TableHead className="table-header-color">
        <TableRow>
          <TableCell
            sx={{
              fontWeight: "bold",
              width: "20%",
              display: {
                xs: "none",
                sm: "table-cell",
              },
            }}
            align="left"
          >
            Investigation
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
              width: "20%",
              display: {
                xs: "none",
                sm: "table-cell",
              },
            }}
            align="left"
          >
            Parameter
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
              width: "15",
              display: {
                xs: "none",
                sm: "table-cell",
              },
            }}
            align="left"
          >
            Specimen
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
              width: "20%",
              display: {
                xs: "none",
                sm: "table-cell",
              },
            }}
            align="center"
          >
            Value
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
              width: "25%",
              display: {
                xs: "none",
                sm: "table-cell",
              },
            }}
            align="left"
          >
            Remarks
          </TableCell>
        </TableRow>
      </TableHead>
    );
  }

  const formattedSlots = appointmentTimeSlots.map((item: any) => ({
    dayName: item.dayName,
    sessionName: item.sessionName,
    start: dayjs(item.slotStart, "HH:mm:ss"),
    end: dayjs(item.slotEnd, "HH:mm:ss"),
  }));

  const groupedSlots = formattedSlots.reduce((acc: any, slot: any) => {
    if (!acc[slot.dayName]) acc[slot.dayName] = {};
    if (!acc[slot.dayName][slot.sessionName])
      acc[slot.dayName][slot.sessionName] = [];
    acc[slot.dayName][slot.sessionName].push(slot);
    return acc;
  }, {});

  const allowedDatesSet = new Set(
    appointmentDate.map((item) =>
      dayjs(item.appointmentDate).format("YYYY-MM-DD"),
    ),
  );

  const disableUnavailableDates = (date: dayjs.Dayjs) => {
    const formattedDate = date.format("YYYY-MM-DD");
    return !allowedDatesSet.has(formattedDate); // return true if the date should be disabled
  };

  const handleClickOpen = (value: boolean, label: string, code: string) => {
    setCustomPopUp({ ...customPopUp, open: value, name: label, code: code });
  };

  const handleClose = () => {
    setCustomPopUp({ ...customPopUp, open: false });
  };

  // HANDLE DELETE WARNING POPUP
  const handleDeleteDilog = (value: any) => {
    setOpenDeleteDilog(value);
  };
  const handleManagePopup = async (code: any, option: any) => {
    setCustomPopUp({ ...customPopUp, open: false });
    setIsLoading(true);
    if (code === "PATIENT_CREATE") {
      const Patient = await getPatientDetailById(option?.patientId);
      handlePatientDataChange(Patient);
      await fetchPatient("", true);
    } else if (code === "REFERRAL_PATIENT_CREATE") {
      methods.setValue(
        "referralPatientId",
        {
          label: option?.label,
          value: option?.patientId.toString(),
        },
        {
          shouldValidate: true,
        },
      );
      methods.setValue("referralPatientName", option?.label, {
        shouldValidate: true,
      });
    } else if (code === "DOCTOR_CREATE") {
      methods.setValue(
        "referralDoctorId",
        {
          label: option?.doctorLable,
          value: option?.doctorId.toString(),
        },
        {
          shouldValidate: true,
        },
      );
      await fetchRefDoctor("", true, "", "");
    } else if (code === "ITEM_CREATE") {
      await fetchItem("", true);
      if (option?.itemId) {
        const newItem = {
          label: option?.itemName,
          value: option?.itemId?.toString(),
          itemId: option?.itemId,
          itemName: option?.itemName,
          displayOrder: methods.getValues("reasonForVisitColl")?.length || 0,
        };
        const currentReasons = methods.getValues("reasonForVisitColl") || [];
        const exists = currentReasons.some(
          (r: any) =>
            r.itemId === option.itemId || r.value === option.itemId.toString(),
        );
        if (!exists) {
          methods.setValue("reasonForVisitColl", [...currentReasons, newItem], {
            shouldValidate: true,
          });
        }
      }
    } else if (code === "MR_CREATE") {
      fetchMedicalRepresentative("", true);
    }
    setIsLoading(false);
  };

  // DEFAULT SCHEMA VALUE FOR FORM
  const initAppointmentData: formInitialValueTypes = {
    appointmentId: 0,
    departmentId: null,
    specialityId: null,
    appointmentTypeCode: "PATIENT",
    appointmentNumber: 0,
    patientId: null,
    firstName: "",
    middleName: "",
    lastName: "",
    patientFirstName: "",
    patientLastName: "",
    patientMiddleName: "",
    dateOfBirth: "",
    preferredLanguage: null,
    maritalStatus: null,
    gender: null,
    medicalRepresentativeId: null,
    guestId: 0,
    consultingDoctorId: null,
    referredByCode: null,
    referredByName: "",
    referralPatientId: null,
    referralPatientName: "",
    referralDoctorId: null,
    underModule: { label: "OPD", value: "OPD" },
    visitTypeCode: null,
    location: "",
    address1: "",
    address2: "",
    city: "",
    postalCode: "",
    countryId: null,
    countryName: null,
    countryRegionId: null,
    countryRegionName: null,
    email: "",
    primaryMobileNumber: "",
    secondaryMobileNumber: "",
    note: "",
    isDeleted: false,
    ageYear: 0,
    ageMonth: 0,
    ageDay: 0,
    companyNameId: null,
    // status: "WAITING",
    status: isWalkIn ? "WAITING" : "SCHEDULED",
    prefix: null,
    emrId: null,
    isEmergency: false,
    weight: null,
    height: null,
    pr: null,
    spO2Amount: null,
    spO2SiteUnit: "RIGHT_UPPER_LIMB",
    bpLeftAmount: null,
    bpRightAmount: null,
    bpDisplayValue: "",
    bpSiteUnit: "RIGHT_UPPER_LIMB",
    appointmentDate: isWalkIn
      ? new Date(Date.now())
      : isCalendar && calendarDate?.date
        ? calendarDate.date
        : null,
    appointmentTime: null,
    slotNumber: 0,
    temperatureAmount: null,
    temperatureUnit: "F",
    temperatureByPalpation: null,
    respiratoryRate: null,
    reasonForVisitColl: [],
    reportParameterColl: [],
    departmentName: null,
    specialityName: null,
    consultingDoctorName: null,
    patientName: null,
    medicalRepresentativeName: null,
    companyName: null,
    referralDoctorName: null,
    visitTypeCodeName: null,
    preferredLanguageName: null,
    maritalStatusName: null,
    genderName: null,
    prefixName: null,
    rbs: null,
    headCircumference: null,
    motherHeight: null,
    fatherHeight: null,
    referredByCodeName: null,
    financialYear: userContext?.financialYearDetail?.financialYear,
    locationId: null,
    locationName: "",
    locationTypeCode: "",
    isOutReach: false,
  };

  const consultingDoctorOptionComponent = (props: any) => {
    const { label, departmentName, specialityName } = props.data;
    return (
      <components.Option {...props}>
        {props.data.__isNew__ ? (
          <Box>{props.children}</Box>
        ) : (
          <Box display="flex" flexDirection="column">
            <Typography component="div" variant="body2" fontWeight={600}>
              {label}
            </Typography>
            <Typography
              component="div"
              variant="caption"
              color="text.secondary"
            >
              ({departmentName || "—"}
              {specialityName ? `, ${specialityName}` : ""})
            </Typography>
          </Box>
        )}
      </components.Option>
    );
  };

  const mergeDateAndTime = (date: Date, time: string) => {
    const [hours, minutes] = time.split(":").map(Number);

    const merged = new Date(date);
    merged.setHours(hours);
    merged.setMinutes(minutes);
    merged.setSeconds(0);

    return merged;
  };

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    appointmentId: Yup.number().required(),
    underModule: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Under Module is required"),
    departmentId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Department is required"),
    departmentName: Yup.string().required(),
    specialityId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Speciality is required"),
    specialityName: Yup.string().required(),
    appointmentTypeCode: Yup.string().required(
      "Appointment Type Code is required",
    ),
    appointmentNumber: Yup.number(),
    patientId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .when(["appointmentTypeCode", "status"], {
        is: (appointmentTypeCode: string, status: string) =>
          appointmentTypeCode === "PATIENT" && status !== "SCHEDULED",
        then: (schema) => schema.required("Patient is required"),
        otherwise: (schema) => schema.nullable(),
      }),
    patientName: Yup.string().when(["appointmentTypeCode", "status"], {
      is: (appointmentTypeCode: string, status: string) =>
        appointmentTypeCode === "PATIENT" && status !== "SCHEDULED",
      then: (schema) => schema.required("Patient is required"),
      otherwise: (schema) => schema.nullable(),
    }),
    medicalRepresentativeId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .when("appointmentTypeCode", {
        is: (appointmentTypeCode: string) =>
          appointmentTypeCode === "MEDICAL_REPRESENTATIVE",
        then: (schema) => schema.required("Medical Representative is required"),
        otherwise: (schema) => schema.nullable(),
      }),
    medicalRepresentativeName: Yup.string().when("appointmentTypeCode", {
      is: (appointmentTypeCode: string) =>
        appointmentTypeCode === "MEDICAL_REPRESENTATIVE",
      then: (schema) => schema.required("Medical Representative is required"),
      otherwise: (schema) => schema.nullable(),
    }),
    companyNameId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .when("appointmentTypeCode", {
        is: (appointmentTypeCode: string) =>
          appointmentTypeCode === "MEDICAL_REPRESENTATIVE",
        then: (schema) => schema.required("Company is required"),
        otherwise: (schema) => schema.nullable(),
      }),
    companyName: Yup.string().when("appointmentTypeCode", {
      is: (appointmentTypeCode: string) =>
        appointmentTypeCode === "MEDICAL_REPRESENTATIVE",
      then: (schema) => schema.required("Company is required"),
      otherwise: (schema) => schema.nullable(),
    }),
    guestId: Yup.number().nullable(),
    consultingDoctorId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Consulting Doctor is required"),
    consultingDoctorName: Yup.string().required(
      "Consulting Doctor is required",
    ),
    referredByCode: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    referredByName: Yup.string().nullable(),
    referralPatientId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    referralPatientName: Yup.string().nullable(),
    referralDoctorId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    referralDoctorName: Yup.string().nullable(),
    visitTypeCode: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    visitTypeCodeName: Yup.string().nullable(),
    prefix: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    prefixName: Yup.string().nullable(),
    emrId: Yup.string().nullable(),
    status: Yup.string(),
    isEmergency: Yup.boolean().required(),
    reasonForVisitColl: Yup.array()
      .of(
        Yup.object().shape({
          displayOrder: Yup.number().required(),
          itemId: Yup.number().required(),
          itemName: Yup.string().required("Reason for visit is required"),
        }),
      )
      .min(1, "At least one reason for visit is required.")
      .required("reason For Visit is required."),
    weight: Yup.number()
      .max(999, "Max weight limit is 999")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    height: Yup.number()
      .max(999, "Max height limit is 999")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    pr: Yup.number()
      .max(999, "Max pulse rate limit is 999")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    respiratoryRate: Yup.number()
      .max(999, "Max respiratory rate limit is 999")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    spO2Amount: Yup.number()
      .max(100, "Max amount required is 100")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    spO2SiteUnit: Yup.string().nullable(),
    bpLeftAmount: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    bpRightAmount: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    bpDisplayValue: Yup.string().nullable(),
    bpSiteUnit: Yup.string().nullable(),
    temperatureAmount: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    temperatureUnit: Yup.string().nullable(),

    temperatureByPalpation: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    firstName: Yup.string().when(["appointmentTypeCode", "status"], {
      is: (appointmentTypeCode: string, status: string) =>
        appointmentTypeCode === "PATIENT" && status === "SCHEDULED",
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.required(),
    }),
    middleName: Yup.string().nullable(),
    lastName: Yup.string().when(["appointmentTypeCode", "status"], {
      is: (appointmentTypeCode: string, status: string) =>
        appointmentTypeCode === "PATIENT" && status === "SCHEDULED",
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.required(),
    }),
    patientMiddleName: Yup.string().nullable(),
    patientFirstName: Yup.string().when(["appointmentTypeCode", "status"], {
      is: (appointmentTypeCode: string, status: string) =>
        appointmentTypeCode === "PATIENT" && status === "SCHEDULED",
      then: (schema) => schema.required("Patient first name is required"),
      otherwise: (schema) => schema.nullable(),
    }),
    patientLastName: Yup.string().when(["appointmentTypeCode", "status"], {
      is: (appointmentTypeCode: string, status: string) =>
        appointmentTypeCode === "PATIENT" && status === "SCHEDULED",
      then: (schema) => schema.required("Patient last name is required"),
      otherwise: (schema) => schema.nullable(),
    }),
    dateOfBirth: Yup.string().nullable(),
    ageYear: Yup.number().nullable(),
    ageMonth: Yup.number().nullable(),
    ageDay: Yup.number().nullable(),
    preferredLanguage: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    preferredLanguageName: Yup.string().nullable(),
    maritalStatus: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    maritalStatusName: Yup.string().nullable(),
    gender: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Marital Status is required"),
    genderName: Yup.string().nullable(),
    location: Yup.string().nullable(),
    address1: Yup.string().nullable(),
    address2: Yup.string().nullable(),
    city: Yup.string().nullable(),
    postalCode: Yup.string().nullable(),
    countryName: Yup.string().nullable(),
    countryRegionName: Yup.string().nullable(),
    countryId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    countryRegionId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    email: Yup.string().email("Invalid email").nullable(),
    primaryMobileNumber: Yup.string().nullable(),
    secondaryMobileNumber: Yup.string().nullable(),
    rbs: Yup.number()
      .max(999, "Max rbs limit is 999")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    headCircumference: Yup.number()
      .max(999, "Max headCircumference limit is 999")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    motherHeight: Yup.number()
      .max(999, "Max MotherHeight limit is 999")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    fatherHeight: Yup.number()
      .max(999, "Max FatherHeight limit is 999")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    note: Yup.string().nullable(),
    appointmentDate: Yup.date().required(),
    appointmentTime: Yup.string().nullable(),
    slotNumber: Yup.number().required("Slot number is required"),
    isDeleted: Yup.boolean().required(),
    isOutReach: Yup.boolean().required(),
    locationId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Location is required"),
    locationName: Yup.string().required("Location is required"),
    locationTypeCode: Yup.string().nullable(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initAppointmentData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  const appointmentReportParameter = useWatch({
    control: methods?.control,
    name: "reportParameterColl",
  });

  const appointmentTypeCode = methods.watch("appointmentTypeCode");

  const appointmentStatus = methods.watch("status");
  const patientIdValue = methods.watch("patientId");
  const tabTypeValue = (curTabType || "").toString().toLowerCase();
  const isScheduledTab = tabTypeValue === "scheduled";
  const allowManualPatientDetails =
    appointmentTypeCode === "PATIENT" && appointmentStatus === "SCHEDULED";

  const patientIdNull = methods.watch("patientId");
  // const showScheduledPatientCard = appointmentTypeCode === "PATIENT" && isScheduledTab;
  const showScheduledPatientCard = isScheduledTab;

  const hasPatientDetails =
    allowManualPatientDetails || Boolean(patientIdValue);
  const isPatientDetailsReadOnly = isEditDisabled || !allowManualPatientDetails;
  const [inputValue, setInputValue] = useState("");
  // GET Appointment DETAIL
  const getDetailAppointmentById = async () => {
    let Appointment: any;
    let params = new URLSearchParams();
    params.append("appointmentId", curAppointmentId.toString());
    params.append("consumerId", userContext?.consumerId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    try {
      const res: any = await GetAppointmentDetail(params);
      if (res?.data?.data) {
        Appointment = res.data.data;
        const apptDateObj = Appointment?.appointmentDateTime
          ? new Date(Appointment.appointmentDateTime)
          : null;

        const reasonForVisit = Appointment?.reasonForVisitColl?.map(
          (reason: any) => ({
            ...reason,
            mode: "U",
            isDeleted: 0,
          }),
        );

        const masterList = methods.getValues("reportParameterColl") || [];
        const reportParameter = masterList.map((master: any) => {
          const saved = Appointment?.reportParameterColl?.find(
            (s: any) =>
              Number(s?.investigationParameterId) ===
              Number(master?.investigationParameterId),
          );
          return {
            ...master,
            ...(saved
              ? {
                  appointmentReportParameterId: saved.appointmentReportParameterId,
                  unitValue: saved.unitValue,
                  remarks: saved.remarks,
                  optionValue: saved.optionValue,
                }
              : {}),
            mode: "U",
            isDeleted: 0,
          };
        });
        
        methods.reset(
          {
            ...initAppointmentData,
            appointmentId: Appointment?.appointmentId || 0,
            appointmentNumber: Appointment?.appointmentNumber || 0,
            departmentId: Appointment?.departmentId
              ? {
                  value: Appointment?.departmentId || 0,
                  label: Appointment?.departmentName || "",
                }
              : null,
            departmentName: Appointment?.departmentName || "",
            specialityId: Appointment?.specialityId
              ? {
                  value: Appointment?.specialityId || 0,
                  label: Appointment?.specialityName || "",
                }
              : null,
            specialityName: Appointment?.specialityName || "",
            underModule: Appointment?.underModule
              ? {
                  label: Appointment?.underModule?.toString() || "",
                  value: Appointment?.underModule?.toString(),
                }
              : null,
            appointmentTypeCode: Appointment?.appointmentTypeCode || "",
            patientId: Appointment?.patientId
              ? {
                  label: Appointment?.patientName || "",
                  value: Appointment?.patientId,
                }
              : null,
            patientName: Appointment?.patientName || "",
            medicalRepresentativeId: Appointment?.medicalRepresentativeId
              ? {
                  label: Appointment?.medicalRepresentativeName || "",
                  value: Appointment?.medicalRepresentativeId,
                }
              : null,
            medicalRepresentativeName:
              Appointment?.medicalRepresentativeName || "",
            companyNameId: Appointment?.companyNameId
              ? {
                  label: Appointment?.companyName || "",
                  value: Appointment?.companyNameId,
                }
              : null,
            companyName: Appointment?.companyName || "",
            guestId: Appointment?.guestId || 0,
            consultingDoctorId: Appointment?.consultingDoctorId
              ? {
                  label: Appointment?.consultingDoctorName || "",
                  value: Appointment?.consultingDoctorId,
                }
              : null,
            consultingDoctorName: Appointment?.consultingDoctorName || "",

            locationId: Appointment?.locationId
              ? {
                  label: Appointment?.locationName || "",
                  value: Appointment?.locationId,
                }
              : null,
            locationName: Appointment?.locationName || "",
            locationTypeCode: Appointment?.locationTypeCode || "",

            referredByCode: Appointment?.referredByCode
              ? {
                  label: Appointment?.referredByCodeName || "",
                  value: Appointment?.referredByCode,
                }
              : null,
            referredByCodeName: Appointment?.referredByCodeName || "",
            referredByName: Appointment?.referredByName || "",
            referralDoctorId: Appointment?.referralDoctorId
              ? {
                  label: Appointment?.referralDoctorName,
                  value: Appointment?.referralDoctorId,
                }
              : null,
            referralDoctorName: Appointment?.referralDoctorName || "",
            referralPatientId: Appointment?.referralPatientId
              ? {
                  label: Appointment?.referralPatientName,
                  value: Appointment?.referralPatientId,
                }
              : null,
            referralPatientName: Appointment?.referralPatientName || "",
            visitTypeCode: Appointment?.visitTypeCode
              ? {
                  label: Appointment?.visitTypeCodeName || "",
                  value: Appointment?.visitTypeCode,
                }
              : null,
            visitTypeCodeName: Appointment?.visitTypeCodeName || "",
            prefix: Appointment?.prefix
              ? {
                  label: Appointment?.prefixName || "",
                  value: Appointment?.prefix,
                }
              : null,
            prefixName: Appointment?.prefixName || "",
            emrId: Appointment?.emrId || "",
            status: Appointment?.status || "",
            isEmergency: Appointment?.isEmergency || false,
            weight: Appointment?.weight,
            height: Appointment?.height,
            pr: Appointment?.pr,
            spO2Amount: Appointment?.spO2Amount,
            spO2SiteUnit: Appointment?.spO2SiteUnit || "",
            bpLeftAmount: Appointment?.bpLeftAmount,
            bpRightAmount: Appointment?.bpRightAmount,
            bpDisplayValue: Appointment?.bpDisplayValue || "/",
            bpSiteUnit: Appointment?.bpSiteUnit || "",
            temperatureAmount: Appointment?.temperatureAmount,
            temperatureUnit: Appointment?.temperatureUnit || "",
            temperatureByPalpation: Appointment?.temperatureByPalpation
              ? {
                  label: Appointment?.temperatureByPalpation,
                  value: Appointment?.temperatureByPalpation,
                }
              : null,
            respiratoryRate: Appointment?.respiratoryRate || 0,
            firstName: Appointment?.firstName || "",
            middleName: Appointment?.middleName || "",
            lastName: Appointment?.lastName || "",
            patientFirstName:
              Appointment?.patientFirstName || Appointment?.firstName || "",
            patientMiddleName:
              Appointment?.patientMiddleName || Appointment?.middleName || "",
            patientLastName:
              Appointment?.patientLastName || Appointment?.lastName || "",
            dateOfBirth: Appointment?.dateOfBirth || null,
            ageDay: Appointment?.ageDay || 0,
            ageMonth: Appointment?.ageMonth || 0,
            ageYear: Appointment?.ageYear || 0,
            preferredLanguage: Appointment?.preferredLanguage
              ? {
                  label: Appointment?.preferredLanguageName || "",
                  value: Appointment?.preferredLanguage,
                }
              : null,
            preferredLanguageName: Appointment?.preferredLanguageName || "",
            maritalStatus: Appointment?.maritalStatus
              ? {
                  label: Appointment?.maritalStatusName || "",
                  value: Appointment?.maritalStatus,
                }
              : null,
            maritalStatusName: Appointment?.maritalStatusName || "",
            gender: Appointment?.gender
              ? {
                  label: Appointment?.genderName || "",
                  value: Appointment?.gender,
                }
              : null,
            genderName: Appointment?.genderName || "",
            location: Appointment?.location || "",
            address1: Appointment?.address1 || "",
            address2: Appointment?.address2 || "",
            city: Appointment?.city || "",
            postalCode: Appointment?.postalCode || "",
            countryId: Appointment?.countryId
              ? {
                  label: Appointment?.countryName || "",
                  value: Appointment?.countryId?.toString(),
                }
              : null,
            countryName: Appointment?.countryName || "",
            countryRegionName: Appointment?.countryRegionName || "",
            countryRegionId: Appointment?.countryRegionId
              ? {
                  label: Appointment?.countryRegionName || "",
                  value: Appointment?.countryRegionId?.toString(),
                }
              : null,
            email: Appointment?.email || "",
            primaryMobileNumber: Appointment?.primaryMobileNumber || "",
            secondaryMobileNumber: Appointment?.secondaryMobileNumber || "",
            rbs: Appointment?.rbs,
            headCircumference: Appointment?.headCircumference,
            motherHeight: Appointment?.motherHeight,
            fatherHeight: Appointment?.fatherHeight,
            note: Appointment.note || "",
            reasonForVisitColl: reasonForVisit || [],
            // appointmentDate: Appointment?.appointmentDate
            //   ? dayjs(Appointment?.appointmentDate)
            //   : null,
            // appointmentDateTime: Appointment?.appointmentDateTime || null,
            appointmentDate: Appointment?.appointmentDatetime
              ? new Date(Appointment?.appointmentDatetime)
              : null,

            appointmentTime: Appointment?.appointmentDatetime
              ? dayjs(Appointment?.appointmentDatetime).format("HH:mm")
              : null,
            slotNumber: Appointment?.slotNumber || 0,
            financialYear: Appointment?.financialYear,
            reportParameterColl: reportParameter || [],
          },

          {
            keepErrors: true,
          },
        );
        setDeletedReasonForVisit([]);
      }
      if (Appointment?.consultingDoctorId) {
        const location = await fetchLocation(
          "",
          true,
          Appointment.consultingDoctorId?.toString() || "",
        );
      }
      if (Appointment?.locationId) {
        await fetchAppointmentAvailabilityDate(
          Appointment.locationId,
          Appointment?.locationTypeCode === "OUTREACH" ? true : false,
        );
      }
      if (Appointment?.appointmentDatetime && Appointment?.locationId) {
        const selectedDate = dayjs(Appointment.appointmentDatetime).format(
          "YYYY-MM-DD",
        );
        const dayName = dayjs(Appointment.appointmentDatetime).format("dddd");
        const slots = await fetchAppointmentAvailability(
          Appointment.locationId,
          selectedDate,
          dayName,
          Appointment?.consultingDoctorId,
        );

        const bookedTime = dayjs(Appointment.appointmentDatetime).format(
          "HH:mm:ss",
        );
        const slotExists = slots?.some((s: any) => s.slotStart === bookedTime);
        if (!slotExists && slots) {
          const bookedSlot = {
            dayName: dayName,
            sessionName: "Booked",
            slotStart: bookedTime,
            slotEnd: dayjs(Appointment.appointmentDatetime)
              .add(30, "minute")
              .format("HH:mm:ss"),
          };
          setAppointmentTimeSlots([...slots, bookedSlot]);
        }
      }
      return Appointment;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // GET DEPARTMENT
  async function fetchDepartment(searchValue: string, onInint: boolean) {
    try {
      let params = new URLSearchParams();
      let departmentColl: any;
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("mode", "DROPDOWN".toString());
      const resultData: any = await GetDepartmentList(params);
      if (resultData?.data?.data?.departmentColl) {
        departmentColl = resultData?.data?.data?.departmentColl;

        if (onInint) setDepartmentColl(departmentColl);

        // if(departmentColl && departmentColl.length === 1)
        // {
        //   let department = departmentColl[0];
        //   methods.reset
        //   ({
        //     ...initAppointmentData,
        //     departmentId:department?.departmentId
        //     ? {
        //         value: department?.departmentId || 0,
        //         label: department?.departmentIdLabel || "",
        //       }
        //     : null,
        //   })
        // }
      }
      return departmentColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FILTER DEPARTMENT
  async function filterDepartment(inputValue: string) {
    let result = await fetchDepartment(inputValue, false);
    return result;
  }

  // DEPARTMENT LOAD ON SEARCH
  const loadDepartmentList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterDepartment(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // GET SPECIALITY
  async function fetchSpeciality(
    departmentIds: string,
    searchValue: string,
    onInint: boolean,
  ) {
    try {
      let params = new URLSearchParams();
      let specialityColl: any;
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("departmentIds", departmentIds);
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("mode", "DROPDOWN".toString());
      const result: any = await GetSpecialityList(params);
      if (result?.data?.data?.specialityColl) {
        specialityColl = result.data.data.specialityColl;
        if (onInint) setSpecialityColl(specialityColl);
      }
      return specialityColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FILTER SPECIALITY
  async function filterSpeciality(inputValue: string) {
    let result = await fetchSpeciality(
      methods?.getValues()?.departmentId?.value || "",
      inputValue,
      false,
    );
    return result;
  }

  // SPECIALITY LOAD ON SEARCH
  const loadSpecialityList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterSpeciality(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // GET APPOINTMENT NUMBER
  const getAppointmentNumber = async (
    doctorId: string,
    appointmentDateValue?: Date | string | null,
  ) => {
    try {
      let appointmentNumber;
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("doctorId", doctorId);
      params.append(
        "dataManagedBy",
        userContext.loginDetail?.userId?.toString(),
      );
      const resolvedDate =
        appointmentDateValue ?? methods.getValues("appointmentDate");
      if (resolvedDate) {
        params.append(
          "appointmentDate",
          dayjs(resolvedDate).format("YYYY-MM-DD"),
        );
      }
      const result: any = await GetAppointmentNumber(params);
      if (result?.data?.data) {
        appointmentNumber = result.data.data;
      }
      return appointmentNumber;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE Appointment
  const saveAppointment = async (value: formInitialValueTypes) => {
    try {
      const reasonForVisitEntries = [
        ...(value?.reasonForVisitColl || []),
        ...deletedReasonForVisit,
      ];
      const reasonForVisitColl = reasonForVisitEntries.map(
        (item: any, index: number) => {
          return {
            displayOrder: item?.displayOrder ?? index,
            itemId: item?.value ?? item?.itemId ?? 0,
            itemName: item?.label ?? item?.itemName ?? "",
            isDeleted: item?.isDeleted ? 1 : 0,
            mode: item?.mode || "I",
            appointmentReasonForVisitId: item?.appointmentReasonForVisitId || 0,
          };
        },
      );
      let result: any = "";
      const isScheduledPatient =
        value.appointmentTypeCode === "PATIENT" && value.status === "SCHEDULED";
      const patientFirstNameValue =
        value.patientFirstName || value.firstName || "";
      const patientLastNameValue =
        value.patientLastName || value.lastName || "";
      const derivedPatientName =
        value.patientName ||
        (isScheduledPatient
          ? [patientFirstNameValue, value.middleName, patientLastNameValue]
              .filter(Boolean)
              .join(" ")
              .trim()
          : "");

      const reportParameterColl = (appointmentReportParameter)
        ?.filter(
          (item: any) =>
            (item?.unitValue !== null &&
              item?.unitValue !== undefined &&
              item?.unitValue?.toString().trim() !== "") ||
            (item?.optionValue !== null &&
              item?.optionValue !== undefined &&
              item?.optionValue?.toString().trim() !== ""),
        )
        ?.map((item: any, index: number) => {
          return {
            appointmentReportParameterId: item?.appointmentReportParameterId ?? 0,
            appointmentId: item?.appointmentId ?? 0,
            consumerId: item?.consumerId ?? userContext?.consumerId ?? 0,
            orgId: item?.orgId ?? userContext?.organizationId ?? 0,
            branchId: item?.branchId ?? userContext?.branchId ?? 0,
            departmentId: item?.departmentId ?? value?.departmentId?.value ?? 0,
            specialityId: item?.specialityId ?? value?.specialityId?.value ?? 0,
            opdId: 0,
            patientId: item?.patientId ?? value?.patientId?.value ?? 0,
            investigationId: item?.investigationId ?? 0,
            investigationName: item?.investigationName ?? "",
            investigationParameterId: item?.investigationParameterId ?? 0,
            parameterName: item?.parameterName ?? "",
            investigationParameterUnitId: item?.investigationParameterUnitId ?? 0,
            unitName: item?.unitName ?? "",
            unitValue: item?.unitValue ?? "",
            specimen: item?.specimen ?? "",
            remarks: item?.remarks ?? "",
            optionValue : item?.optionValue ?? "",
            displayOrder: index,
            isDeleted: item?.isDeleted ?? false,
          };
        });

      let formData = {
        appointmentId: value.appointmentId,
        opdId: opd?.opdId || 0,
        consumerId: userContext?.consumerId,
        orgId: userContext?.organizationId,
        branchId: userContext?.branchId,

        departmentId: value?.departmentId?.value,
        departmentName: value?.departmentName || "",
        specialityId: value?.specialityId?.value,
        specialityName: value?.specialityName || "",

        underModule: value?.underModule?.value || "",
        appointmentTypeCode: value.appointmentTypeCode,
        appointmentNumber: value.appointmentNumber,
        patientId: value.patientId?.value || 0,
        patientName: derivedPatientName,
        medicalRepresentativeId: value.medicalRepresentativeId?.value || 0,
        medicalRepresentativeName: value.medicalRepresentativeName || "",
        companyNameId: value?.companyNameId?.value || 0,
        companyName: value?.companyName || "",
        guestId: value?.guestId || 0,
        consultingDoctorId: value.consultingDoctorId?.value || 0,
        locationId: value.locationId?.value || 0,
        consultingDoctorName: value.consultingDoctorName || "",
        referredByCode: value.referredByCode?.value || "",
        referredByCodeName: value.referredByCodeName || "",
        referredByName: value.referredByName,
        referralPatientId: value.referralPatientId?.value || 0,
        referralPatientName: value.referralPatientName || "",
        referralDoctorId: value.referralDoctorId?.value || 0,
        referralDoctorName: value.referralDoctorName || "",
        row_managed_by: userContext?.loginDetail?.userId,
        visitTypeCode: value.visitTypeCode?.value || "",
        visitTypeCodeName: value.visitTypeCodeName || "",
        prefix: value?.prefix?.value || "",
        prefixName: value?.prefixName || "",
        emrId: value?.emrId || "",
        status: value?.status || "",
        isEmergency: value?.isEmergency || false,
        weight: value.weight || 0,
        height: value.height || 0,
        pr: value.pr || 0,
        spO2Amount: value?.spO2Amount || 0,
        spO2SiteUnit: value?.spO2SiteUnit || "",
        bpLeftAmount: value?.bpLeftAmount || 0,
        bpRightAmount: value?.bpRightAmount || 0,
        bpDisplayValue: value?.bpDisplayValue,
        bpSiteUnit: value?.bpSiteUnit || "",
        temperatureAmount: value?.temperatureAmount || 0,
        temperatureUnit: value?.temperatureUnit || "",
        temperatureByPalpation: value?.temperatureByPalpation?.value || "",
        respiratoryRate: value?.respiratoryRate || 0,
        firstName: patientFirstNameValue.trim() || "",
        middleName: value.middleName?.trim() || "",
        lastName: patientLastNameValue.trim() || "",

        patientFirstName:
          value.patientFirstName?.trim() || value.firstName?.trim() || "",
        patientLastName:
          value.patientLastName?.trim() || value.lastName?.trim() || "",
        patientMiddleName:
          value.patientMiddleName?.trim() || value.firstName?.trim() || "",

        dateOfBirth: value?.dateOfBirth || null,
        ageYear: value?.ageYear || 0,
        ageMonth: value?.ageMonth || 0,
        ageDay: value?.ageDay || 0,
        preferredLanguage: value.preferredLanguage?.value,
        preferredLanguageName: value.preferredLanguageName,
        maritalStatus: value.maritalStatus?.value || "",
        maritalStatusName: value.maritalStatusName || "",
        gender: value.gender?.value,
        genderName: value.genderName || "",
        location: value?.location?.trim() || "",
        address1: value?.address1?.trim() || "",
        address2: value?.address2?.trim() || "",
        city: value?.city?.trim() || "",
        postalCode: value?.postalCode?.trim() || "",
        countryId: value.countryId?.value,
        countryName: value.countryName || "",
        countryRegionName: value.countryRegionName || "",
        countryRegionId: value.countryRegionId?.value,
        email: value?.email || "",
        primaryMobileNumber: value?.primaryMobileNumber || "",
        secondaryMobileNumber: value?.secondaryMobileNumber || "",
        rbs: value.rbs || 0,
        headCircumference: value.headCircumference || 0,
        motherHeight: value.motherHeight || 0,
        fatherHeight: value.fatherHeight || 0,
        note: value.note || "",
        // appointmentDate: isWalkIn
        //   ? new Date(Date.now())
        //   : value.appointmentDate,
        appointmentDateTime: isWalkIn
          ? new Date(Date.now())
          : value.appointmentDate && value.appointmentTime
            ? mergeDateAndTime(value.appointmentDate, value.appointmentTime)
            : new Date(),
        slotNumber: value?.slotNumber || 0,
        reasonForVisitJson: JSON.stringify(reasonForVisitColl),
        reportParameterJson:
            reportParameterColl.length > 0
              ? JSON.stringify(reportParameterColl)
              : null,
        financialYear: value?.financialYear,
        dataManagedBy: userContext.loginDetail.userId,
      };
      if (curAppointmentId > 0) {
        result = await UpdateAppointment(formData);
      } else {
        result = await CreateAppointment(formData);
      }
      if (result?.data) {
        if (!isForPopUp) {
          navigate(-1);
        } else {
          if (result?.data) {
            handleClosePopUp("APPOINTMENT_CREATE", result?.data);
          }
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // GET PREFERRED LANGUAGE
  const fetchLanguage = async () => {
    try {
      const resultDataCollection: any = await GetLanguageListForSelect();
      if (resultDataCollection?.data?.data) {
        let languageCollection = resultDataCollection.data?.data;
        setLanguageData(languageCollection);
        setIsLoading(false);
      }
    } catch (e: any) {
      setIsLoading(false);
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // GET PATIENT DETAIL
  const getPatientDetailById = async (patientId) => {
    let Patient: any;
    let params = new URLSearchParams();
    params.append("patientId", patientId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    try {
      const res: any = await GetPatientDetail(params);

      if (res?.data?.data) {
        Patient = res.data.data;
      }
      return Patient;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  const handlePatientDataChange = (Patient: any) => {
    let birthDate = new Date(Patient?.dateOfBirth);
    let { Year, Month, Day } = getAge(birthDate);

    // STORING THE LAST OPD DATE
    if (Patient?.lastOpdCaseDate) {
      const lastOpdDate = new Date(Patient?.lastOpdCaseDate);
      // GETTING THE DIFFRENCE
      const difference = lastOpdVisitTime(lastOpdDate);
      setLastOpdDateDiffrence(difference);

      // GETTING THE DAY MONTH AND YEAR OF LAST OPD
      const lastOpdDay = String(lastOpdDate.getDate()).padStart(2, "0");
      const lastOpdMonth = String(lastOpdDate.getMonth() + 1).padStart(2, "0");
      const lastOpdYear = lastOpdDate.getFullYear();
      const formatted = `${lastOpdDay}-${lastOpdMonth}-${lastOpdYear}`;
      setLastOpdCaseDate(formatted);
    } else {
      setLastOpdCaseDate("");
    }

    methods.reset(
      {
        ...methods.getValues(),
        patientId: Patient?.patientId
          ? {
              label: Patient?.label,
              value: Patient?.patientId,
            }
          : null,
        patientName: Patient?.label || "",
        emrId: Patient?.patientNumber || "",
        firstName: Patient?.firstName || "",
        patientFirstName: Patient?.firstName || "",
        middleName: Patient?.middleName || "",
        lastName: Patient?.lastName || "",
        patientLastName: Patient?.lastName || "",
        gender: Patient?.gender
          ? {
              label: Patient?.genderName,
              value: Patient?.gender,
            }
          : null,
        genderName: Patient?.genderName || "",
        maritalStatus: Patient?.maritalStatus
          ? {
              label: Patient?.maritalStatusName,
              value: Patient?.maritalStatus,
            }
          : null,
        maritalStatusName: Patient?.maritalStatusName || "",
        preferredLanguage: Patient?.preferredLanguage
          ? {
              label: Patient?.preferredLanguageName || "",
              value: Patient?.preferredLanguage,
            }
          : null,
        preferredLanguageName: Patient?.preferredLanguageName || "",
        dateOfBirth: Patient?.dateOfBirth || "",
        ageYear: Year || 0,
        ageMonth: Month || 0,
        ageDay: Day || 0,
        location: Patient?.location || "",
        address1: Patient?.address1 || "",
        address2: Patient?.address2 || "",
        city: Patient?.city || "",
        postalCode: Patient?.postalCode || "",
        countryName: Patient?.countryName || "",
        countryRegionName: Patient?.countryRegionName || "",
        countryId: Patient?.countryId
          ? {
              label: Patient?.countryName,
              value: Patient?.countryId,
            }
          : null,
        countryRegionId: Patient?.countryRegionId
          ? {
              label: Patient?.countryRegionName,
              value: Patient?.countryRegionId,
            }
          : null,
        email: Patient?.email || "",
        primaryMobileNumber: Patient?.primaryMobileNumber || "",
        secondaryMobileNumber: Patient?.secondaryMobileNumber || "",

        referralDoctorId:
          Patient?.referralDoctorId && Patient?.referralDoctorName
            ? {
                value: Patient.referralDoctorId,
                label: Patient.referralDoctorName,
              }
            : null,
        referralDoctorName: Patient?.referralDoctorName || "",
        referredByCode: Patient?.referredByCode
          ? {
              value: Patient.referredByCode,
              label:
                Patient?.referredByCode === "REFERRAL_DOCTOR"
                  ? "Referral Doctor"
                  : Patient?.referredByCode
                      ?.replace(/_/g, " ")
                      ?.replace(/\b\w/g, (c) => c.toUpperCase()) || "",
            }
          : null,
        referralPatientId:
          Patient?.referralPatientId && Patient?.referralPatientName
            ? {
                value: Patient.referralPatientId,
                label: Patient.referralPatientName,
              }
            : null,
        referralPatientName: Patient?.referralPatientName || "",
        referredByName: Patient?.referredByName || "",
      },
      {
        keepErrors: true,
      },
    );
    if (Patient?.patientId) {
      methods.clearErrors([
        "patientId",
        "patientFirstName",
        "patientLastName",
        "firstName",
        "lastName",
      ]);
    }
  };

  // GET MedicalRepresentative DETAIL
  const getMedicalRepresentativeDetailById = async (
    medicalRepresentativeId,
  ) => {
    let MedicalRepresentative: any;
    let params = new URLSearchParams();

    params.append(
      "medicalRepresentativeId",
      medicalRepresentativeId?.toString(),
    );
    params.append("branchId", userContext?.branchId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());
    try {
      const res: any = await GetMedicalRepresentativeDetail(params);
      if (res?.data?.data) {
        MedicalRepresentative = res.data.data;
      }
      return MedicalRepresentative;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  const handleMedicalRepresentativeDataChange = (
    MedicalRepresentative: any,
  ) => {
    const genderconvertlabel =
      genderOptions.find((x) => x.value === MedicalRepresentative?.gender) ||
      null;
    const maritalStatusconvertlabel =
      maritalStatusOptions.find(
        (x) => x.value === MedicalRepresentative?.maritalStatus,
      ) || null;
    const prefixconvertlabel =
      prefixOptions.find((x) => x.value === MedicalRepresentative?.prefix) ||
      null;

    let today = new Date();
    let birthDate = new Date(MedicalRepresentative?.dateOfBirth);
    let ageYear: any = MedicalRepresentative?.dateOfBirth
      ? today.getFullYear() - birthDate.getFullYear()
      : 0;
    let ageMonth: any = MedicalRepresentative?.dateOfBirth
      ? today.getMonth() - birthDate.getMonth()
      : 0;
    let ageDay: any = MedicalRepresentative?.dateOfBirth
      ? today.getDate() - birthDate.getDate()
      : 0;

    methods.reset(
      {
        ...methods.getValues(),
        medicalRepresentativeId: MedicalRepresentative?.medicalRepresentativeId
          ? {
              label: MedicalRepresentative?.medicalRepresentativeName,
              value: MedicalRepresentative?.medicalRepresentativeId,
            }
          : null,
        medicalRepresentativeName:
          MedicalRepresentative?.medicalRepresentativeName || "",
        prefix: prefixconvertlabel || null,
        prefixName: prefixconvertlabel?.label || "",
        firstName: MedicalRepresentative?.firstName || "",
        middleName: MedicalRepresentative?.middleName || "",
        lastName: MedicalRepresentative?.lastName || "",
        dateOfBirth: MedicalRepresentative?.dateOfBirth || "",
        ageYear: ageYear || 0,
        ageMonth: ageMonth || 0,
        ageDay: ageDay || 0,
        preferredLanguage: MedicalRepresentative?.preferredLanguage
          ? {
              label: MedicalRepresentative?.preferredLanguageLable,
              value: MedicalRepresentative?.preferredLanguage,
            }
          : null,
        preferredLanguageName:
          MedicalRepresentative?.preferredLanguageLable || "",
        maritalStatus: maritalStatusconvertlabel || null,
        maritalStatusName: maritalStatusconvertlabel?.label || null,
        gender: genderconvertlabel || null,
        genderName: genderconvertlabel?.label || null,
        companyNameId: MedicalRepresentative?.medicineCompanyId
          ? {
              label: MedicalRepresentative?.medicineCompanyNameLable,
              value: MedicalRepresentative?.medicineCompanyId?.toString(),
            }
          : null,
        companyName: MedicalRepresentative?.medicineCompanyNameLable || "",
        location: MedicalRepresentative?.location || "",
        address1: MedicalRepresentative?.address1 || "",
        address2: MedicalRepresentative?.address2 || "",
        city: MedicalRepresentative?.city || "",
        postalCode: MedicalRepresentative?.postalCode || "",
        countryName: MedicalRepresentative?.countryName || "",
        countryRegionName: MedicalRepresentative?.countryRegionName || "",
        countryId: MedicalRepresentative?.countryId
          ? {
              label: MedicalRepresentative?.countryName || "",
              value: MedicalRepresentative?.countryId?.toString(),
            }
          : null,
        countryRegionId: MedicalRepresentative?.countryRegionId
          ? {
              label: MedicalRepresentative?.countryRegionName || "",
              value: MedicalRepresentative?.countryRegionId?.toString(),
            }
          : null,
        email: MedicalRepresentative?.email || "",
        primaryMobileNumber: MedicalRepresentative?.primaryMobileNumber || "",
        secondaryMobileNumber:
          MedicalRepresentative?.secondaryMobileNumber || "",
      },
      {
        keepErrors: true,
      },
    );
  };

  // GET REF DOCTOR
  const fetchRefDoctor = async (
    searchValue: string,
    oninit: boolean,
    departmentIds: string,
    specialityIds: string,
  ) => {
    try {
      let doctorCollection = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("doctorTypeCodes", "REFERRAL_DOCTOR");
      params.append("departmentIds", "");
      params.append("specialityIds", "");
      params.append("mode", "DROPDOWN".toString());
      const doctorData: any = await GetDoctorList(params);
      if (doctorData?.data?.data?.doctorColl) {
        doctorCollection = doctorData.data.data.doctorColl;
        if (oninit) setRefDoctorColl(doctorCollection);
      }
      return doctorCollection;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // FILTER REF DOCTOR
  async function filterRefDoctor(
    inputValue: string,
    departmentIds: string,
    specialityIds: string,
  ) {
    let result = await fetchRefDoctor(
      inputValue,
      false,
      departmentIds,
      specialityIds,
    );
    return result;
  }

  // REF DOCTOR LOAD ON SEARCH
  const loadRefDoctorList = (
    inputValue: string,
    departmentIds: string,
    specialityIds: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterRefDoctor(inputValue, departmentIds, specialityIds).then((r) => {
        if (r && typeof callback === "function") {
          callback(r);
        }
      });
    } else {
      if (typeof callback === "function") {
        callback([]);
      }
    }
  };

  // GET CONSULT DOCTOR
  const fetchConsultingDoctor = async (
    searchValue: string,
    oninit: boolean,
    locationId: string | null,
    locationTypeCode?: string,
  ) => {
    try {
      let doctorCollection = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("locationId", locationId || "");
      params.append("locationTypeCode", locationTypeCode || "");
      params.append("isConsultingDoctor", "true");
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      const doctorData: any = await GetLocationtoTeamMemberList(params);
      if (doctorData?.data?.data?.teamMemberColl) {
        doctorCollection = doctorData.data.data.teamMemberColl;
        if (oninit) setConsultingDoctorColl(doctorCollection);
      }
      if (doctorCollection?.length === 1) {
        let doc: any = doctorCollection[0];
        if (doc?.value) {
          const appointmentNumber = await getAppointmentNumber(doc?.value);
          methods.setValue(
            "consultingDoctorId",
            doc?.value
              ? {
                  value: doc?.value || 0,
                  label: doc?.label || "",
                }
              : null,
            { shouldValidate: true },
          );
          methods.setValue("consultingDoctorName", doc?.label || "", {
            shouldValidate: true,
          });
          methods.setValue(
            "departmentId",
            doc?.departmentId
              ? {
                  value: doc?.departmentId || 0,
                  label: doc?.departmentName || "",
                }
              : null,
            { shouldValidate: true },
          );
          methods.setValue("departmentName", doc?.departmentName || "", {
            shouldValidate: true,
          });
          methods.setValue(
            "specialityId",
            doc?.specialityId
              ? {
                  value: doc?.specialityId || 0,
                  label: doc?.specialityName || "",
                }
              : null,
            { shouldValidate: true },
          );
          methods.setValue("specialityName", doc?.specialityName || "", {
            shouldValidate: true,
          });
          methods.setValue(
            "appointmentNumber",
            appointmentNumber?.autoNumber || 0,
            { shouldValidate: true },
          );
          methods.setValue("slotNumber", appointmentNumber?.slotNumber || 0, {
            shouldValidate: true,
          });
        }
        if (!isWalkIn) {
          await fetchAppointmentAvailabilityDate(
            methods.getValues("locationId")?.value,
            methods.getValues("locationTypeCode") === "OUTREACH" ? true : false,
          );
        } else {
          setAppointmentDate([]);
        }
        methods.trigger();
      }
      return doctorCollection;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // FILTER REF DOCTOR
  async function filterConsultingDoctor(inputValue: string) {
    let result = await fetchConsultingDoctor(inputValue, false, null);
    return result;
  }

  // REF DOCTOR LOAD ON SEARCH
  const loadConsultingDoctorList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterConsultingDoctor(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // GET LIST OF MR
  const fetchMedicalRepresentative = async (
    searchValue: string,
    onInit: boolean,
  ) => {
    try {
      let mrCollection: any = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("mode", "DROPDOWN".toString());
      const result: any = await GetMedicalRepresentativeList(params);
      if (result?.data?.data?.medicalRepresentativeColl) {
        mrCollection = result.data.data.medicalRepresentativeColl;
        if (onInit) setMrColl(mrCollection);
      }
      return mrCollection;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // FILTER MR
  async function filterMedicalRepresentative(inputValue: string) {
    let result = await fetchMedicalRepresentative(inputValue, false);
    return result;
  }

  // MR LOAD ON SEARCH
  const loadMedicalRepresentativeList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterMedicalRepresentative(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // GET ITEMS
  async function fetchItem(searchValue: string, oninit: boolean) {
    try {
      let itemCollection = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("mode", "DROPDOWN".toString());
      params.append("productTypeCodes", "HSN,SAC");
      const itemData: any = await GetItemList(params);
      if (itemData?.data?.data?.itemColl) {
        itemCollection = itemData.data.data.itemColl;
        if (oninit) setItemColl(itemCollection);
      }
      return itemCollection;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FILTER ITEM
  async function filterItem(inputValue: string) {
    let result = await fetchItem(inputValue, false);
    return result;
  }

  // ITEM LOAD ON SEARCH
  const loadItemList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterItem(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // GET COMPANY NAME
  async function fetchMedicineCompany(searchValue: string, onint: boolean) {
    try {
      let medicineCompanyColl = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("mode", "DROPDOWN".toString());
      const result: any = await GetMedicineCompanyList(params);
      if (result?.data?.data?.medicineCompanyColl) {
        medicineCompanyColl = result.data.data.medicineCompanyColl;
        if (onint) setMedicineCompanyColl(medicineCompanyColl);
      }
      return medicineCompanyColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FILTER COMPANY NAME
  async function filterMedicineCompany(inputValue: string) {
    let result = await fetchMedicineCompany(inputValue, false);
    return result;
  }

  // COMPANY NAME LOAD ON SEARCH
  const loadMedicineCompanyList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterMedicineCompany(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // GET PATIENTS
  const fetchPatient = async (searchValue: string, oninit: boolean) => {
    try {
      let patientCollection = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("mode", "DROPDOWN".toString());
      const patientData: any = await GetPatientList(params);
      if (patientData?.data?.data?.patientColl) {
        patientCollection = patientData.data.data.patientColl;
        if (oninit) setPatientColl(patientCollection);
      }
      return patientCollection;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // FILTER PATIENT
  async function filterPatient(inputValue: string) {
    let result = await fetchPatient(inputValue, false);
    return result;
  }

  // PATIENT LOAD ON SEARCH
  const loadPatientList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterPatient(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // LOAD REFERRED PATIENT
  const loadReferredPatientList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterPatient(inputValue?.trim()).then((r) => {
        const filtered = filteredPatientColl(r);
        callback(filtered);
      });
    } else {
      callback([]);
    }
  };

  const patientCollOptionComponent = (props: any) => {
    return (
      <components.Option {...props}>
        {props.data.__isNew__ && <Box>{props.children}</Box>}
        {props.data && !props.data.__isNew__ && (
          <Box>
            <Grid container>
              <Grid item xs={12}>
                <Typography component="div" variant="body2" fontWeight={600}>
                  #{props?.data?.patientNumber + " - "}
                  {props?.children}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  component="div"
                  // className="fileds-label-text"
                  // sx={{ color: "#0009" }}
                  variant="caption"
                  color="text.secondary"
                >
                  {props.data?.location &&
                    (props.data?.location === props.data?.city
                      ? props.data?.location
                      : `${props.data?.location}${
                          props.data?.city ? ", " + props.data?.city : ""
                        }`)}
                  {/* {props.data?.location
                    ? " ," + props?.data?.primaryMobileNumber
                    : ""} */}
                  {props?.data?.primaryMobileNumber
                    ? " (" + props?.data?.primaryMobileNumber + " )"
                    : ""}
                  {/* {props?.data?.dateOfBirth
                    ? " (" +
                      props?.data?.genderName +
                      "/ " +
                      props?.data?.ageYear +
                      "Y /" +
                      props?.data?.ageMonth +
                      "M /" +
                      props?.data?.ageDay +
                      "D" +
                      ")"
                    : ""} */}
                  {props?.data?.dateOfBirth && (
                    <>
                      {(() => {
                        const parts = [];

                        if (props?.data?.ageYear > 0)
                          parts.push(`${props.data.ageYear}Y`);

                        if (props?.data?.ageMonth > 0)
                          parts.push(`${props.data.ageMonth}M`);

                        if (props?.data?.ageDay > 0)
                          parts.push(`${props.data.ageDay}D`);

                        return parts.length
                          ? ` (${props.data.genderName}/ ${parts.join(" / ")})`
                          : "";
                      })()}
                    </>
                  )}

                  {/* {props?.data?.dateOfBirth
                    ? " (" +
                      dayjs(props?.data?.dateOfBirth).format("DD-MM-YYYY") +
                      ")"
                    : ""} */}
                  {/* {props.data?.location || ""}{" "}
                  {props?.data?.city ? ", " + props?.data?.city : ""} */}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
      </components.Option>
    );
  };

  // // GET COUNTRIES
  // async function fetchCountry() {
  //   try {
  //     let params = new URLSearchParams();
  //     const countryData: any = await GetCountryListForSelect(params);
  //     if (countryData?.data?.data) {
  //       let countyCollection = countryData.data.data;
  //       setCountryData(countyCollection);
  //     }
  //   } catch (e: any) {
  //     updateUserContext({
  //       ...userContext,
  //       isAlert: true,
  //       alertMessage:
  //         typeof e === "string" ? (e as string) : (e?.message as string),
  //       alertType: AlertType.Error,
  //     });
  //   }
  // }

  // FETCH STATE FOR COUNTRY
  // async function fetchCountryRegionData(countryId) {
  //   try {
  //     let params = new URLSearchParams();
  //     params.append("countryId", countryId);
  //     const localCountryRegionData: any = await GetCountryRegionListForSelect(
  //       params
  //     );
  //     if (localCountryRegionData?.data?.data) {
  //       let stateCollection = localCountryRegionData.data.data;
  //       setCountryRegionData(stateCollection);
  //     }
  //   } catch (e: any) {
  //     updateUserContext({
  //       ...userContext,
  //       isAlert: true,
  //       alertMessage:
  //         typeof e === "string" ? (e as string) : (e?.message as string),
  //       alertType: AlertType.Error,
  //     });
  //   }
  // }

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    setIsLoading(true);
    await saveAppointment(data);
    setIsLoading(false);
  };
  const onError = (errors) => {
    console.log("Form errors:", errors);
    // yaha tum toast, alert, ya UI handling kar sakte ho
  };
  // HANDLE BACK
  const handleBack = () => {
    if (isForPopUp) {
      handleClosePopUp("CLOSE");
    } else {
      navigate(-1);
    }
  };

  async function fetchLocation(
    searchValue: string,
    oninit: boolean,
    teamMemberId: number,
  ) {
    try {
      let params = new URLSearchParams();
      let locationCollection = [];
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      // params.append("branchId", userContext.branchId?.toString());
      params.append("teamMemberId", teamMemberId?.toString());
      // params.append("teamMemberId", userContext.doctorId?.toString() || "");
      params.append("isContainBranch", "true");
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      // const response: any = await GetLocationList(params);
      const response: any = await GetTeamMemberLocationList(params);
      if (response?.data?.data?.locationColl) {
        locationCollection = response.data.data.locationColl;
        if (oninit) setLocationData(locationCollection);

        const defaultLocation = locationCollection.find(
          (x: any) => Number(x.locationId) === Number(userContext?.locationId),
        );
        if (defaultLocation) {
          methods.setValue("locationId", {
            value: defaultLocation?.locationId || 0,
            label: defaultLocation?.locationName || "",
          });

          methods.setValue("locationName", defaultLocation?.locationName || "");
          methods.setValue(
            "locationTypeCode",
            defaultLocation?.locationTypeCode || "",
          );
          methods.setValue(
            "isOutReach",
            defaultLocation?.locationTypeCode === "OUTREACH" ? true : false,
          );
        }
        await fetchConsultingDoctor(
          "",
          true,
          defaultLocation?.locationId,
          defaultLocation?.locationTypeCode,
        );
      }
      return locationCollection;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  async function fetchAppointmentAvailability(
    locationId: any,
    date: string,
    dayName?: string,
    consultingDoctorId?: number,
  ) {
    const result = methods.getValues();
    try {
      let params = new URLSearchParams();
      let locationCollection = [];
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("locationId", locationId?.toString());
      // params.append(
      //   "linkId",
      //   methods?.getValues()?.consultingDoctorId?.value || "",
      // );
      params.append("linkId", consultingDoctorId?.toString() || "");

      params.append("dayName", dayName?.toString() || "");
      params.append("date", date?.toString() || "");
      params.append("isOutReach", methods?.getValues()?.isOutReach?.toString());
      const response: any = await GetAppointmentAvailabilityList(params);
      if (response?.data?.data?.appointmentAvailabilityColl) {
        locationCollection = response.data.data.appointmentAvailabilityColl;
        setAppointmentTimeSlots(locationCollection);
      }
      return locationCollection;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  async function fetchAppointmentAvailabilityDate(
    locationId: any,
    isOutReach: boolean,
  ) {
    try {
      let params = new URLSearchParams();
      let dateCollection = [];
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("locationId", locationId?.toString());
      params.append(
        "teamMemberId",
        methods?.getValues()?.consultingDoctorId?.value || "",
      );
      params.append("daysAhead", "1095"); // 3 years
      params.append("isOutReach", isOutReach?.toString());
      const response: any = await GetAppointmentAvailabilityDateList(params);
      if (response?.data?.data?.appointmentAvailabilityDateColl) {
        dateCollection = response.data.data.appointmentAvailabilityDateColl;
        setAppointmentDate(dateCollection);
      }
      return dateCollection;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // GET PREFERRED LANGUAGE
  const getLanguageList = async () => {
    try {
      let languageCollection = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext?.consumerId?.toString());
      params.append("orgId", userContext?.organizationId?.toString());
      params.append("branchId", userContext?.branchId?.toString());
      const languageResultData: any = await GetBranchLanguageList(params);
      if (languageResultData?.data?.data) {
        languageCollection = languageResultData.data?.data;
        setLanguageResultData(languageCollection);
      }
      return languageCollection;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  const getReportParameterList = async () => {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", curPage?.toString());
      params.append("pageSize", "5000");
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", query || "");
      const result: any = await GetAppointmentReportParameterList(params);
      if (result?.data?.data?.appointmentReportParameterColl) {
        const resultData: ResultData = result?.data?.data;
        let appointmentReportParameterColl = resultData?.appointmentReportParameterColl || [];
        setResultData(resultData);

        const unitOptions = appointmentReportParameterColl?.flatMap((item: any) => {
          const options = [];
          if (item?.unitName?.trim()) {
            options?.push({
              investigationParameterId: item.investigationParameterId,
              investigationParameterUnitId:
                item.investigationParameterUnitId ?? 0,
              unitName: item.unitName,
              isOptional: false,
            });
          }
          if (item?.optionalUnit?.trim()) {
            options.push({
              investigationParameterId: item.investigationParameterId,
              investigationParameterUnitId:
                item.investigationParameterUnitId ?? 0,
              unitName: item.optionalUnit,
              isOptional: true,
            });
          }
          return options;
        });
        setUnitColl(unitOptions);
        const reportParameterDetailColl = appointmentReportParameterColl?.map(
          (item: any) => {
            const defaultUnit =
              unitOptions?.find(
                (u: any) =>
                  u?.investigationParameterId === item?.investigationParameterId &&
                  !u?.isOptional,
              )?.unitName || item?.unitName || "";
            return {
              appointmentReportParameterId: item?.appointmentReportParameterId ?? 0,
              appointmentId: item?.appointmentId ?? 0,
              consumerId: item?.consumerId ?? 0,
              orgId: item?.orgId ?? 0,
              branchId: item?.branchId ?? 0,
              departmentId: item?.departmentId ?? 0,
              specialityId: item?.specialityId ?? 0,
              patientId: item?.patientId ?? 0,
              investigationId: item?.investigationId ?? 0,
              investigationName: item?.investigationName ?? "",
              investigationParameterId: item?.investigationParameterId ?? 0,
              parameterName: item?.parameterName ?? "",
              investigationParameterUnitId: item?.investigationParameterUnitId ?? 0,
              unitName: defaultUnit,
              unitValue: item?.unitValue ?? "",
              optionValue: item?.optionValue ?? "",
              specimen: item?.specimen ?? "",
              displayOrder: item?.displayOrder ?? 0,
              isDeleted: item?.isDeleted ?? false,
            };
          },
        );
        methods.setValue("reportParameterColl", reportParameterDetailColl, {
          shouldValidate: false,
        });
      }
      setShowSkeleton(false);
      setIsLoading(false);
    } catch (e) {
      setShowSkeleton(false);
      setIsLoading(false);
      console.log("err:", e);
    }
  };

  const getInvestigationParameterRangeList = async (
    searchValue: string,
    oninit: boolean,
  ) => {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", "5000");
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("mode", "LIST");

      const result: any = await GetInvestigationParameterRangeList(params);
      let rangeColl: any[] = [];
      if (result?.data?.data?.rangeColl) {
        rangeColl = result?.data?.data?.rangeColl;
        if (oninit) setInvestigationParameterRangeColl(rangeColl);
      }
      return rangeColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: typeof e === "string" ? e : e?.message,
        alertType: AlertType.Error,
      });
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      await fetchLanguage();
      // await fetchCountry();
      await fetchPatient("", true);
      await fetchRefDoctor("", true, "", "");
      await fetchItem("", true);
      await fetchMedicalRepresentative("", true);
      await fetchMedicineCompany("", true);
      await fetchLocation("", true, userContext.doctorId || 0);
      await getLanguageList();
      await getReportParameterList();
      await getInvestigationParameterRangeList("", true);
      if (curAppointmentId > 0) {
        const result = await getDetailAppointmentById();
      } else if (prefilledPatient) {
        const patientDetail = await getPatientDetailById(
          prefilledPatient.patientId,
        );
        if (patientDetail) {
          handlePatientDataChange(patientDetail);
        }
      } else if (isForPopUp && opd?.patientId) {
        const patientDetail = await getPatientDetailById(opd.patientId);
        if (patientDetail) {
          handlePatientDataChange(patientDetail);
        }
      }

      if (!curAppointmentId && prefilledAppointmentData) {
        if (prefilledAppointmentData.reasonForVisit) {
          const reasonOption = prefilledAppointmentData.reasonForVisit.itemId;
          methods.setValue("reasonForVisitColl", [
            {
              ...reasonOption,
              displayOrder: 0,
              itemId: reasonOption.value,
              itemName: reasonOption.label,
              isDeleted: 0,
              mode: "I",
              appointmentReasonForVisitId: 0,
            },
          ]);
        }
        if (prefilledAppointmentData.locationId) {
          const loc = prefilledAppointmentData.locationId;
          methods.setValue("locationId", loc);
          methods.setValue("locationName", loc.label || "");
          methods.setValue("locationTypeCode", loc.locationTypeCode || "");
          methods.setValue(
            "isOutReach",
            loc.locationTypeCode === "OUTREACH" ? true : false,
          );
        }
        if (prefilledAppointmentData.appointmentDate) {
          methods.setValue(
            "appointmentDate",
            new Date(prefilledAppointmentData.appointmentDate),
          );
        }
        if (prefilledAppointmentData.appointmentTime) {
          methods.setValue(
            "appointmentTime",
            prefilledAppointmentData.appointmentTime,
          );
        }

        // Set doctor, department and speciality from opd if available
        if (opd?.departmentId) {
          methods.setValue("departmentId", {
            value: opd.departmentId.toString(),
            label: opd.departmentName || "",
          });
          methods.setValue("departmentName", opd.departmentName || "");
        }
        if (opd?.specialityId) {
          methods.setValue("specialityId", {
            value: opd.specialityId.toString(),
            label: opd.specialityName || "",
          });
          methods.setValue("specialityName", opd.specialityName || "");
        }

        // Set doctor from opd if available to ensure availability can be fetched
        if (opd?.consultingDoctorId) {
          const doctorObj = {
            value: opd.consultingDoctorId.toString(),
            label: opd.consultingDoctorName || "",
          };
          methods.setValue("consultingDoctorId", doctorObj);
          methods.setValue(
            "consultingDoctorName",
            opd.consultingDoctorName || "",
          );

          // Fetch next appointment number for the doctor
          getAppointmentNumber(opd.consultingDoctorId).then((apptNumData) => {
            if (apptNumData) {
              methods.setValue(
                "appointmentNumber",
                apptNumData.autoNumber || 0,
              );
              methods.setValue("slotNumber", apptNumData.slotNumber || 0);
            }
          });
        }

        // Fetch referral info from previous appointment if possible
        if (isForPopUp && opd?.appointmentId) {
          const params = new URLSearchParams();
          params.append("appointmentId", opd.appointmentId.toString());
          params.append("consumerId", userContext?.consumerId?.toString());
          params.append("orgId", userContext?.organizationId?.toString());
          params.append("branchId", userContext?.branchId?.toString());
          GetAppointmentDetail(params).then((res: any) => {
            if (res?.data?.data) {
              const prevAppt = res.data.data;
              if (prevAppt.referredByCode) {
                methods.setValue("referredByCode", {
                  value: prevAppt.referredByCode,
                  label: prevAppt.referredByCodeName || "",
                });
                methods.setValue(
                  "referredByCodeName",
                  prevAppt.referredByCodeName || "",
                );
              }
              if (prevAppt.referralDoctorId) {
                methods.setValue("referralDoctorId", {
                  value: prevAppt.referralDoctorId.toString(),
                  label: prevAppt.referralDoctorName || "",
                });
                methods.setValue(
                  "referralDoctorName",
                  prevAppt.referralDoctorName || "",
                );
              }
              if (prevAppt.referralPatientId) {
                methods.setValue("referralPatientId", {
                  value: prevAppt.referralPatientId.toString(),
                  label: prevAppt.referralPatientName || "",
                });
                methods.setValue(
                  "referralPatientName",
                  prevAppt.referralPatientName || "",
                );
              }
              if (prevAppt.referredByName) {
                methods.setValue("referredByName", prevAppt.referredByName);
              }
            }
          });
        }

        // If location and date are also pre-filled, fetch availability automatically
        if (
          prefilledAppointmentData.locationId &&
          prefilledAppointmentData.appointmentDate
        ) {
          const date = new Date(prefilledAppointmentData.appointmentDate);
          const formattedDate = dayjs(date).format("YYYY-MM-DD");
          const dayName = dayjs(date).format("dddd");
          await fetchAppointmentAvailabilityDate(
            prefilledAppointmentData.locationId.value,
            prefilledAppointmentData.locationId.locationTypeCode === "OUTREACH",
          );
          await fetchAppointmentAvailability(
            prefilledAppointmentData.locationId.value,
            formattedDate,
            dayName,
            opd.consultingDoctorId,
          );
        }
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log(e);
    }
  };

  // HANDLE DELETE
  async function deleteRow(appointmentId: any) {
    try {
      handleDeleteDilog(false);
      let DeleteData = {
        appointmentId: appointmentId?.toString(),
        consumerId: userContext?.consumerId.toString(),
        orgId: userContext?.organizationId.toString(),
        branchId: userContext?.branchId.toString(),
        status: "CANCELLED",
        dataManagedBy: userContext?.loginDetail?.userId?.toString(),
      };
      const result: any = await UpdateAppointmentStatus(DeleteData);
      if (result) {
        if (isCalendar) {
          handleClosePopUp("APPOINTMENT_CREATE", result?.data);
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result.message,
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setParam({ ...param, tabType: newValue });
    if (isForPopUp) {
      return;
    }
    const tabCode = newValue === 0 ? "basic-information" : "report-parameter";
    navigate(
      {
        search: `?q=${encodeURIComponent(
          edService.encrypt(curAppointmentId.toString()) || "",
        )}&t=${tabCode}`,
      },
      { replace: true, state: location.state },
    );
  };

  const showAppointmentTabs =
    isWalkIn || curAppointmentId > 0 || (isForPopUp && curTabType === "waiting");

  // HANDLE CHECK ROW
  const handleCheckSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = resultData?.appointmentReportParameterColl?.map(
        (n: any) => n?.reportParameterId || 0,
      );
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  // SORTING TABLE
  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number,
  ) {
    const stabilizedThis = array?.map(
      (el, index) => [el, index] as [T, number],
    );
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  }

  // DESCENDING COMPERISION
  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  // GET COMPERISION
  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string },
  ) => number {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  // ON CHANGE PAGE
  const handleChangePageNew = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    if (curPage !== value) {
      navigate(`?page=${value}&rpPage=${encodeURIComponent(query)}`);
    }
  };

  // ON SERCH
  function searchValue(val: any) {
    val = val.trim();
    // ENCODE THE SEACH VALUE
    navigate(`?page=1&rpPage=${encodeURIComponent(val)}`);
  }

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [curAppointmentId, curTabType, param, curPage, query]);

  const filteredPatientColl = useCallback(
    (options: any) => {
      return (
        options?.filter(
          (x: any) =>
            x.value?.toString() !==
            methods.getValues("patientId")?.value?.toString(),
        ) || []
      );
    },
    [methods.getValues("patientId")],
  );

  return (
    <Box>
      <Box className={"main-container"}>
        {isForPopUp === false && (
          <Grid container className="page-header">
            <Grid item xs={6} sm={6} md={6}>
              <Box
                sx={{ display: "flex", height: "100%", alignItems: "center" }}
              >
                <CustomBreadcrumb />
              </Box>
            </Grid>
            <Grid item xs={6} sm={6} md={6}>
              <Box className="page-header-icon">
                <Button
                  color="primary"
                  variant="outlined"
                  type="button"
                  className="secondary-button"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                >
                  Back
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit, onError)} action="">
            <Grid container gap={2}>
              {showAppointmentTabs && (
                <Grid item xs={12} sx={{ mt: 0 }}>
                  <Tabs
                    value={param.tabType}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    indicatorColor="primary"
                    aria-label="doctor"
                    TabIndicatorProps={{ sx: { backgroundColor: " #2e67a3" } }}
                    sx={{
                      maxWidth: "100%",
                      "& button:hover": {
                        backgroundColor: (t) =>
                          t.palette.mode === "dark"
                            ? "rgba(255,255,255,0.08)"
                            : "#f1f6fe",
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                      },
                    }}
                  >
                    <Tab
                      sx={{ textTransform: "capitalize !important" }}
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <span>Basic Information</span>
                        </Box>
                      }
                    />
                    <Tab
                      sx={{ textTransform: "capitalize !important" }}
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <span>Report Parameter</span>
                        </Box>
                      }
                    />
                  </Tabs>
                </Grid>
              )}

              {showScheduledPatientCard &&
                methods.getValues("appointmentTypeCode") === "PATIENT" && (
                  <Grid item xs={12} className={"group-container"}>
                    <Grid item container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6">Patient Details</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={6}>
                        <div className="input-group">
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.primaryMobileNumber
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Registered Mobile Number (WhatsApp)
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <PhoneInput
                              id="primaryMobileNumber"
                              international
                              countryCallingCodeEditable={false}
                              defaultCountry="IN"
                              disabled={isPatientDetailsReadOnly}
                              {...methods.register("primaryMobileNumber")}
                              error={
                                methods.formState.errors.primaryMobileNumber
                                  ?.message
                              }
                              className={`phone-input ${
                                methods.formState.errors.primaryMobileNumber
                                  ? "error"
                                  : ""
                              }`}
                              onChange={(e) => {
                                methods.setValue(
                                  "primaryMobileNumber",
                                  e || "",
                                );
                              }}
                              value={methods.getValues()?.primaryMobileNumber}
                              placeholder="Enter phone number"
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {
                              methods.formState.errors.primaryMobileNumber
                                ?.message
                            }
                          </Typography>
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6} md={6}></Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.prefix
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Prefix
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="prefix"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"prefix"}
                                  FieldName={"prefix"}
                                  options={prefixOptions}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={async (e: any) => {
                                    if (
                                      methods
                                        ?.getValues()
                                        ?.prefix?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "prefix",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                      methods.setValue(
                                        "prefixName",
                                        e?.value ? e?.label : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                    }
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.prefix?.message}
                          </Typography>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.patientFirstName
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Patient First Name *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              disabled={isPatientDetailsReadOnly}
                              size="small"
                              {...methods.register("patientFirstName")}
                              error={
                                !!methods.formState.errors.patientFirstName
                              }
                              className={`text-input ${
                                methods.formState.errors.patientFirstName
                                  ? "error"
                                  : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.patientFirstName?.message}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.patientMiddleName
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Patient Middle Name
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              disabled={isPatientDetailsReadOnly}
                              size="small"
                              {...methods.register("patientMiddleName")}
                              error={
                                !!methods.formState.errors.patientMiddleName
                              }
                              className={`text-input ${
                                methods.formState.errors.patientMiddleName
                                  ? "error"
                                  : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.patientMiddleName?.message}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.patientLastName
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Patient Last Name *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              disabled={isPatientDetailsReadOnly}
                              size="small"
                              {...methods.register("patientLastName")}
                              error={!!methods.formState.errors.patientLastName}
                              className={`text-input ${
                                methods.formState.errors.patientLastName
                                  ? "error"
                                  : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.patientLastName?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.dateOfBirth
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Date of Birth
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <DatePickerComponent
                              format="DD-MM-YYYY"
                              disabled={isPatientDetailsReadOnly}
                              mode="date"
                              slotProps={{ textField: { size: "small" } }}
                              value={
                                methods?.getValues()?.dateOfBirth
                                  ? dayjs(methods?.getValues()?.dateOfBirth)
                                  : null
                              }
                              onChange={(e: any) => {
                                if (e !== null && isValid(e.$d)) {
                                  let today = new Date(Date.now());
                                  let birthDate = new Date(e.$d);
                                  let Year: any =
                                    today.getFullYear() -
                                    birthDate.getFullYear();
                                  let Month: any =
                                    today.getMonth() - birthDate.getMonth();
                                  let Day: any =
                                    today.getDate() - birthDate.getDate();

                                  let savedDateFormate: any = format(
                                    e.$d,
                                    "yyyy-MM-dd",
                                  );
                                  methods.setValue(
                                    "dateOfBirth",
                                    savedDateFormate,
                                  );
                                  methods.setValue("ageYear", Year);
                                  methods.setValue("ageMonth", Month);
                                  methods.setValue("ageDay", Day);
                                } else {
                                  methods.setValue("dateOfBirth", "");
                                }
                              }}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.ageYear
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Age
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <Grid container spacing={1}>
                              <Grid item xs={4} md={4} xl={3}>
                                <CustomTextField
                                  placeholder=""
                                  autoCapitalize="none"
                                  type="text"
                                  fullWidth
                                  size="small"
                                  disabled={isPatientDetailsReadOnly}
                                  {...methods.register("ageYear", {
                                    onChange: (e) => {
                                      let today = new Date(Date.now());
                                      let Year: any =
                                        today.getFullYear() - e.target.value;
                                      methods.setValue(
                                        "dateOfBirth",
                                        `${Year}-${today.getMonth()}-${today.getDate()}`,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                    },
                                  })}
                                  error={!!methods.formState.errors.ageYear}
                                  className={`text-input `}
                                  inputProps={{
                                    maxLength: 2,
                                  }}
                                  InputProps={{
                                    onKeyPress: (event) => {
                                      if (!/[0-9]/.test(event.key)) {
                                        event.preventDefault();
                                      }
                                    },
                                    endAdornment: (
                                      <span style={{ color: "grey" }}>Y</span>
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid item xs={4} md={4} xl={3}>
                                <CustomTextField
                                  placeholder=""
                                  autoCapitalize="none"
                                  type="text"
                                  fullWidth
                                  size="small"
                                  disabled={isPatientDetailsReadOnly}
                                  {...methods.register("ageMonth", {
                                    onChange: (e) => {
                                      let today = new Date(Date.now());
                                      let Year: any =
                                        today.getFullYear() -
                                        methods.getValues("ageYear");
                                      let Month: any =
                                        today.getMonth() - e.target.value;
                                      methods.setValue(
                                        "dateOfBirth",
                                        `${Year}-${Month}-${today.getDate()}`,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                    },
                                  })}
                                  error={!!methods.formState.errors.ageMonth}
                                  className={`text-input `}
                                  inputProps={{
                                    maxLength: 2,
                                  }}
                                  InputProps={{
                                    onKeyPress: (event) => {
                                      if (!/[0-9]/.test(event.key)) {
                                        event.preventDefault();
                                      }
                                    },
                                    endAdornment: (
                                      <span style={{ color: "grey" }}>M</span>
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid item xs={4} md={4} xl={3}>
                                <CustomTextField
                                  placeholder=""
                                  autoCapitalize="none"
                                  type="text"
                                  fullWidth
                                  size="small"
                                  disabled={isPatientDetailsReadOnly}
                                  {...methods.register("ageDay", {
                                    onChange: (e) => {
                                      let today = new Date(Date.now());
                                      let Year: any =
                                        today.getFullYear() -
                                        methods.getValues("ageYear");
                                      let Month: any =
                                        today.getMonth() -
                                        methods.getValues("ageMonth");
                                      let Day: any =
                                        today.getDate() - e.target.value;

                                      methods.setValue(
                                        "dateOfBirth",
                                        `${Year}-${Month}-${Day}`,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                    },
                                  })}
                                  error={!!methods.formState.errors.ageDay}
                                  className={`text-input `}
                                  inputProps={{
                                    maxLength: 2,
                                  }}
                                  InputProps={{
                                    onKeyPress: (event) => {
                                      if (!/[0-9]/.test(event.key)) {
                                        event.preventDefault();
                                      }
                                    },
                                    endAdornment: (
                                      <span style={{ color: "grey" }}>D</span>
                                    ),
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.ageYear?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.gender
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Gender *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="gender"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"gender"}
                                  FieldName={"gender"}
                                  options={genderOptions}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  isDisabled={isPatientDetailsReadOnly}
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={(e) => {
                                    if (
                                      methods
                                        ?.getValues()
                                        ?.gender?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "gender",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                    }
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.gender?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.location
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Location / Area
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              disabled={isPatientDetailsReadOnly}
                              {...methods.register("location")}
                              error={!!methods.formState.errors.location}
                              className={`text-input ${
                                methods.formState.errors.location ? "error" : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.location?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.city
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            City
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              disabled={isPatientDetailsReadOnly}
                              {...methods.register("city")}
                              error={!!methods.formState.errors.city}
                              className={`text-input ${
                                methods.formState.errors.city ? "error" : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.city?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.countryId
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Country
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="countryId"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"countryId"}
                                  FieldName={"countryId"}
                                  options={countryData}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  isDisabled={isPatientDetailsReadOnly}
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={async (e: any) => {
                                    if (
                                      methods
                                        ?.getValues()
                                        ?.countryId?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "countryId",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                      methods.setValue(
                                        "countryRegionId",
                                        null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                      if (e?.value !== null) {
                                        setIsLoading(true);
                                        await fetchCountryRegionData(e?.value);
                                        setIsLoading(false);
                                      } else {
                                        setCountryRegionData([]);
                                      }
                                    }
                                  }}
                                  className={`text-input ${
                                    methods.formState.errors.countryId
                                      ? "error"
                                      : ""
                                  }`}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.countryId?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.countryRegionId
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            State
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="countryRegionId"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"countryRegionId"}
                                  FieldName={"countryRegionId"}
                                  options={countryRegionData}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  isDisabled={isPatientDetailsReadOnly}
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={(e) => {
                                    methods.setValue(
                                      "countryRegionId",
                                      e?.value ? e : null,
                                      {
                                        shouldValidate: true,
                                      },
                                    );
                                  }}
                                  className={`text-input ${
                                    methods.formState.errors.countryRegionId
                                      ? "error"
                                      : ""
                                  }`}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.countryRegionId?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.preferredLanguage
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Preferred Language
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="preferredLanguage"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"preferredLanguage"}
                                  FieldName={"preferredLanguage"}
                                  options={languageResultData}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  isDisabled={isPatientDetailsReadOnly}
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={(e: any) => {
                                    if (
                                      methods
                                        ?.getValues()
                                        ?.preferredLanguage?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "preferredLanguage",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                      methods.setValue(
                                        "preferredLanguageName",
                                        e?.value ? e?.label : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                    }
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {
                              methods.formState.errors.preferredLanguage
                                ?.message
                            }
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.note
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Please share anything that will help prepare for our
                            meeting.
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              disabled={isPatientDetailsReadOnly}
                              multiline
                              rows={3}
                              {...methods.register("note")}
                              error={!!methods.formState.errors.note}
                              className={`text-input ${
                                methods.formState.errors.note ? "error" : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.note?.message}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                )}

              {param.tabType === 0 && (
                <>
                  <Grid item xs={12} className={"group-container"}>
                    <Grid item container xs={12} spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6">Basic Information</Typography>
                      </Grid>
                      {lgd && (
                        <Grid item xs={12}>
                          <Typography
                            className="font-size-24 bold-600"
                            sx={{
                              textAlign: "right",
                            }}
                          >
                            Queue No:{" "}
                            {methods.getValues()?.appointmentNumber === 0
                              ? "-"
                              : methods.getValues()?.appointmentNumber}
                          </Typography>
                        </Grid>
                      )}
                      {!showScheduledPatientCard && (
                        <Grid item xs={12} sm={12} lg={6}>
                          <Box
                            sx={{
                              display: "flex",
                              height: "100%",
                              alignItems: smd ? "flex-start" : "end",
                              flexDirection: smd ? "column" : "row",
                              justifyContent: "flex-start",
                              gap: smd ? 0 : 2,
                            }}
                          >
                            <FormControl>
                              <FormLabel className={`fileds-label-text`}>
                                Type*{" "}
                              </FormLabel>
                              <Controller
                                control={methods.control}
                                name="appointmentTypeCode"
                                render={({ field }) => {
                                  return (
                                    <RadioGroup
                                      row
                                      {...field}
                                      onChange={(e) => {
                                        methods.setValue(
                                          "appointmentTypeCode",
                                          e.target.value,
                                          {
                                            shouldValidate: true,
                                          },
                                        );
                                        if (e.target.value !== "PATIENT") {
                                          methods.setValue("isEmergency", false, {
                                            shouldValidate: true,
                                          });
                                        }
                                      }}
                                    >
                                      <FormControlLabel
                                        value="PATIENT"
                                        control={<Radio />}
                                        label="Patient"
                                        disabled={isEditDisabled}
                                        className={`fileds-label-text`}
                                      />
                                      <FormControlLabel
                                        value="MEDICAL_REPRESENTATIVE"
                                        control={<Radio />}
                                        label="Medical Representative"
                                        disabled={isEditDisabled}
                                        className={`fileds-label-text `}
                                      />
                                    </RadioGroup>
                                  );
                                }}
                              />
                            </FormControl>
                            {methods.getValues("appointmentTypeCode") ===
                              "PATIENT" &&
                              !isCalendar && (
                                <FormControl
                                  sx={{
                                    pb: smd ? 0 : "4px",
                                  }}
                                >
                                  <FormControlLabel
                                    control={
                                      <SkeletonWrapper
                                        isLoading={isLoading}
                                        className="skeleton-input"
                                      >
                                        <Checkbox
                                          {...methods.register("isEmergency", {
                                            onChange(event) {
                                              methods.setValue(
                                                "isEmergency",
                                                event.target.checked,
                                                {
                                                  shouldValidate: true,
                                                },
                                              );
                                            },
                                          })}
                                          checked={methods.getValues()?.isEmergency}
                                        />
                                      </SkeletonWrapper>
                                    }
                                    label="Emergency"
                                  />
                                  <Typography className="validation-message">
                                    {methods.formState.errors.isEmergency?.message}
                                  </Typography>
                                </FormControl>
                              )}
                          </Box>
                        </Grid>
                      )}
                      {!lgd && !showScheduledPatientCard && (
                        <Grid item xs={12} sm={6} md={6}>
                          <Typography
                            className="font-size-24 bold-600"
                            sx={{
                              textAlign: "right",
                            }}
                          >
                            Queue No:{" "}
                            {methods.getValues()?.appointmentNumber === 0
                              ? "-"
                              : methods.getValues()?.appointmentNumber}
                          </Typography>
                        </Grid>
                      )}
                      {/* {methods.getValues("appointmentTypeCode") === "PATIENT" && (
                        <Grid item xs={12} sm={6} lg={3}>
                          <FormControl fullWidth>
                            <FormLabel
                              className={`fileds-label-text  ${
                                methods.formState.errors.underModule
                                  ? "validation-message"
                                  : ""
                              }`}
                            >
                              Under Module *
                            </FormLabel>
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-input"
                            >
                              <Controller
                                control={methods.control}
                                name="underModule"
                                render={({ field }) => (
                                  <CustomSelect
                                    {...field}
                                    isDisabled={isEditDisabled}
                                    isNotCreatable={true}
                                    id={"underModule"}
                                    FieldName={"underModule"}
                                    options={underModules}
                                    placeholder="Under Module"
                                    openMenuOnFocus
                                    isClearable
                                    isSearchable
                                    menuPortalTarget={document.querySelector(
                                      "body"
                                    )}
                                    handleChange={(e) => {
                                      methods.setValue(
                                        "underModule",
                                        e !== null ? e : null,
                                        { shouldValidate: true }
                                      );
                                    }}
                                    className={`text-input ${
                                      methods.formState.errors.underModule
                                        ? "error"
                                        : ""
                                    }`}
                                  />
                                )}
                              />
                            </SkeletonWrapper>
                            <Typography className="validation-message">
                              {methods.formState.errors.underModule?.message}
                            </Typography>
                          </FormControl>
                        </Grid>
                      )} */}
                      {userContext.loginDetail.userRole !== "user" &&
                        userContext?.locationCount > 1 && (
                          <Grid item xs={12} sm={6} lg={3}>
                            <FormControl fullWidth>
                              <FormLabel
                                className={`fileds-label-text  ${
                                  methods.formState.errors.locationId
                                    ? "validation-message"
                                    : ""
                                }`}
                              >
                                Location *
                              </FormLabel>
                              <SkeletonWrapper
                                isLoading={isLoading}
                                className="skeleton-input"
                              >
                                <Controller
                                  control={methods.control}
                                  name={`locationId`}
                                  render={({ field }) => (
                                    <CustomSelect
                                      {...field}
                                      isNotCreatable
                                      id="locationId"
                                      FieldName="locationId"
                                      options={locationData}
                                      isDisabled={
                                        curAppointmentId > 0 ||
                                        userContext.loginDetail.userRole === "user"
                                      }
                                      handleChange={async (e: any) => {
                                        if (
                                          e?.value !==
                                          methods.getValues("locationId.value")
                                        ) {
                                          methods.setValue(
                                            "locationId",
                                            e?.value ? e : null,
                                            {
                                              shouldValidate: true,
                                            },
                                          );
                                          methods.setValue(
                                            "locationName",
                                            e?.label ? e?.label : null,
                                            {
                                              shouldValidate: true,
                                            },
                                          );
                                          methods.setValue(
                                            "locationTypeCode",
                                            e?.locationTypeCode
                                              ? e?.locationTypeCode
                                              : null,
                                            {
                                              shouldValidate: true,
                                            },
                                          );

                                          // Clear consulting doctor when location changes
                                          methods.setValue(
                                            "consultingDoctorId",
                                            null,
                                            {
                                              shouldValidate: true,
                                            },
                                          );
                                          methods.setValue(
                                            "consultingDoctorName",
                                            null,
                                            {
                                              shouldValidate: true,
                                            },
                                          );
                                          methods.setValue("departmentId", null, {
                                            shouldValidate: true,
                                          });
                                          methods.setValue("departmentName", null, {
                                            shouldValidate: true,
                                          });
                                          methods.setValue("specialityId", null, {
                                            shouldValidate: true,
                                          });
                                          methods.setValue("specialityName", null, {
                                            shouldValidate: true,
                                          });
                                          methods.setValue(
                                            "isOutReach",
                                            e?.locationTypeCode === "OUTREACH"
                                              ? true
                                              : false,
                                            {
                                              shouldValidate: true,
                                            },
                                          );

                                          setAppointmentDate([]);
                                          const docColl =
                                            await fetchConsultingDoctor(
                                              "",
                                              true,
                                              e?.locationId,
                                              e?.locationTypeCode,
                                            );
                                        }
                                      }}
                                    />
                                  )}
                                />
                              </SkeletonWrapper>
                              <Typography className="validation-message">
                                {methods.formState.errors.locationId?.message}
                              </Typography>
                            </FormControl>
                          </Grid>
                        )}
                      {userContext?.locationCount > 1 && (
                        <Grid item xs={12} sm={6} lg={3}>
                          <FormControl fullWidth>
                            <FormLabel
                              className={`fileds-label-text  ${
                                methods.formState.errors.consultingDoctorId
                                  ? "validation-message"
                                  : ""
                              }`}
                            >
                              Consulting Doctor *
                            </FormLabel>
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-input"
                            >
                              <Controller
                                control={methods.control}
                                name="consultingDoctorId"
                                render={({ field }) => (
                                  <CustomAsyncSelect
                                    {...field}
                                    isNotCreatable={true}
                                    id={"consultingDoctorId"}
                                    FieldName={"consultingDoctorId"}
                                    // placeholder="Search or create Consulting Doctor"
                                    placeholder="Search Consulting Doctor"
                                    defaultOptions={consultingDoctorColl}
                                    openMenuOnFocus
                                    isClearable
                                    isSearchable
                                    isDisabled={curAppointmentId > 0}
                                    loadOptions={loadConsultingDoctorList}
                                    menuPortalTarget={document.querySelector(
                                      "body",
                                    )}
                                    CustomOptionComponent={
                                      consultingDoctorOptionComponent
                                    }
                                    handleChange={async (e: any) => {
                                      const newDoctorId = e?.value || null;
                                      if (
                                        methods
                                          ?.getValues()
                                          ?.consultingDoctorId?.value?.toString() !==
                                        e?.value?.toString()
                                      ) {
                                        let appointmentNumber: any;
                                        if (e?.value) {
                                          setIsLoading(true);
                                          appointmentNumber =
                                            await getAppointmentNumber(e?.value);
                                          setIsLoading(false);
                                        }

                                        methods.reset(
                                          {
                                            ...methods.getValues(),
                                            appointmentNumber:
                                              appointmentNumber?.autoNumber || 0,
                                            slotNumber:
                                              appointmentNumber?.slotNumber || 0,
                                            consultingDoctorId: e?.value ? e : null,
                                            consultingDoctorName: e?.value
                                              ? e?.label
                                              : null,
                                            departmentId: e?.value
                                              ? {
                                                  value: e?.departmentId || 0,
                                                  label: e?.departmentName || "",
                                                }
                                              : null,
                                            departmentName: e?.value
                                              ? e?.departmentName
                                              : null,
                                            specialityId: e?.value
                                              ? {
                                                  value: e?.specialityId || 0,
                                                  label: e?.specialityName || "",
                                                }
                                              : null,
                                            specialityName: e?.value
                                              ? e?.specialityName
                                              : null,
                                          },
                                          {
                                            keepErrors: true,
                                            keepDirty: true,
                                            keepDirtyValues: true,
                                          },
                                        );

                                        if (newDoctorId && !isWalkIn) {
                                          await fetchAppointmentAvailabilityDate(
                                            methods.getValues("locationId")?.value,
                                            methods.getValues(
                                              "locationTypeCode",
                                            ) === "OUTREACH"
                                              ? true
                                              : false,
                                            // e?.value,
                                            // e?.locationTypeCode === "OUTREACH"
                                            //   ? true
                                            //   : false,
                                          );
                                          const appointmentDate =
                                            methods.getValues("appointmentDate");
                                          if (appointmentDate) {
                                            const dateTime = dayjs(appointmentDate);
                                            const dayName = dateTime.format("dddd");
                                            const selectedDate =
                                              dateTime.format("YYYY-MM-DD");
                                            await fetchAppointmentAvailability(
                                              // e?.value,
                                              methods.getValues("locationId")
                                                ?.value,
                                              selectedDate,
                                              dayName,
                                              methods.getValues(
                                                "consultingDoctorId",
                                              )?.value,
                                            );
                                          }
                                        } else {
                                          setAppointmentDate([]);
                                        }
                                        // ✅ Immediately re-run form validation
                                        await methods.trigger(undefined, {
                                          shouldFocus: false,
                                        });
                                      }
                                    }}
                                    className={`text-input ${
                                      methods.formState.errors.consultingDoctorId
                                        ? "error"
                                        : ""
                                    }`}
                                  />
                                )}
                              />
                            </SkeletonWrapper>
                            <Typography className="validation-message">
                              {methods.formState.errors.consultingDoctorId?.message}
                            </Typography>
                          </FormControl>
                        </Grid>
                      )}

                      <Grid
                        item
                        sm={6}
                        md={
                          methods.getValues("appointmentTypeCode") === "PATIENT"
                            ? 9
                            : 12
                        }
                        display={{
                          xs: "none",
                          sm:
                            methods.getValues("appointmentTypeCode") === "PATIENT"
                              ? "none"
                              : "grid",
                          lg: "grid",
                        }}
                      ></Grid>

                      {methods.getValues("appointmentTypeCode") === "PATIENT" && (
                        <>
                          {/* <Grid item xs={12} sm={6} lg={3}>
                            <FormControl fullWidth>
                              <FormLabel
                                className={`fileds-label-text  ${
                                  methods.formState.errors.departmentId
                                    ? "validation-message"
                                    : ""
                                }`}
                              >
                                Department *
                              </FormLabel>
                              <SkeletonWrapper
                                isLoading={isLoading}
                                className="skeleton-input"
                              >
                                <Controller
                                  control={methods.control}
                                  name="departmentId"
                                  render={({ field }) => (
                                    <CustomAsyncSelect
                                      {...field}
                                      isNotCreatable={true}
                                      id={"departmentId"}
                                      FieldName={"departmentId"}
                                      isDisabled
                                      defaultOptions={departmentColl}
                                      openMenuOnFocus
                                      isClearable
                                      isSearchable
                                      loadOptions={loadDepartmentList}
                                      menuPortalTarget={document.querySelector(
                                        "body"
                                      )}
                                      handleChange={async (e) => {
                                        if (
                                          methods
                                            ?.getValues()
                                            ?.departmentId?.value?.toString() !==
                                          e?.value?.toString()
                                        ) {
                                          methods.setValue(
                                            "departmentId",
                                            e?.value ? e : null,
                                            {
                                              shouldValidate: true,
                                            }
                                          );
                                          methods.setValue("specialityId", null, {
                                            shouldValidate: true,
                                          });
                                          if (e?.value) {
                                            setIsLoading(true);
                                            await fetchSpeciality(
                                              e.value,
                                              "",
                                              true
                                            );
                                            setIsLoading(false);
                                          } else {
                                            setSpecialityColl([]);
                                          }
                                        }
                                      }}
                                    />
                                  )}
                                />
                              </SkeletonWrapper>
                              <Typography className="validation-message">
                                {methods.formState.errors.departmentId?.message}
                              </Typography>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6} lg={3}>
                            <FormControl fullWidth>
                              <FormLabel
                                className={`fileds-label-text  ${
                                  methods.formState.errors.specialityId
                                    ? "validation-message"
                                    : ""
                                }`}
                              >
                                Speciality *
                              </FormLabel>
                              <SkeletonWrapper
                                isLoading={isLoading}
                                className="skeleton-input"
                              >
                                <Controller
                                  control={methods.control}
                                  name="specialityId"
                                  render={({ field }) => (
                                    <CustomAsyncSelect
                                      {...field}
                                      isNotCreatable={true}
                                      id={"specialityId"}
                                      FieldName={"specialityId"}
                                      defaultOptions={specialityColl}
                                      openMenuOnFocus
                                      isClearable
                                      isSearchable
                                      isDisabled
                                      loadOptions={loadSpecialityList}
                                      menuPortalTarget={document.querySelector(
                                        "body"
                                      )}
                                      handleChange={(e) => {
                                        methods.setValue(
                                          "specialityId",
                                          e?.value ? e : null,
                                          {
                                            shouldValidate: true,
                                          }
                                        );
                                      }}
                                      className={`text-input ${
                                        methods.formState.errors.specialityId
                                          ? "error"
                                          : ""
                                      }`}
                                    />
                                  )}
                                />
                              </SkeletonWrapper>
                              <Typography className="validation-message">
                                {methods.formState.errors.specialityId?.message}
                              </Typography>
                            </FormControl>
                          </Grid> */}
                          <Grid
                            item
                            lg={6}
                            display={{
                              md: "grid",
                              lg: "none",
                            }}
                          />
                          {!showScheduledPatientCard && (
                            <Grid item xs={12} sm={6} lg={6}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.underModule
                                      ? "validation-message"
                                      : ""
                                  }
                                  `}
                                >
                                  Patient *
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <Controller
                                    control={methods.control}
                                    name={"patientId"}
                                    render={({ field }) => (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: 1,
                                          alignItems: "center",
                                        }}
                                      >
                                        <Box sx={{ flex: 1 }}>
                                          <CustomAsyncSelect
                                            {...field}
                                            isNotCreatable={false}
                                            id={"patientId"}
                                            placeholder="Search or create Patient"
                                            FieldName={"patientId"}
                                            defaultOptions={patientColl}
                                            isDisabled={isEditDisabled}
                                            openMenuOnFocus
                                            isClearable
                                            isSearchable
                                            loadOptions={loadPatientList}
                                            menuPortalTarget={document.querySelector(
                                              "body",
                                            )}
                                            handleChange={async (e: any) => {
                                              if (
                                                methods
                                                  ?.getValues()
                                                  ?.patientId?.value?.toString() !==
                                                e?.value?.toString()
                                              ) {
                                                // UPDATE VALUE
                                                methods.setValue(
                                                  "patientId",
                                                  e?.value ? e : null,
                                                  {
                                                    shouldValidate: true,
                                                  },
                                                );

                                                methods.setValue(
                                                  "patientName",
                                                  e?.value ? e.label : null,
                                                  {
                                                    shouldValidate: true,
                                                  },
                                                );
                                                handlePatientDataChange(e);
                                              }
                                            }}
                                            onCreateOption={(inputvalue) => {
                                              handleClickOpen(
                                                true,
                                                inputvalue,
                                                "PATIENT",
                                              );
                                            }}
                                            CustomOptionComponent={
                                              patientCollOptionComponent
                                            }
                                            className={`text-input ${
                                              methods.formState.errors.patientId
                                                ? "error"
                                                : ""
                                            }`}
                                          />
                                        </Box>
                                        <IconButton
                                          aria-label="Edit patient"
                                          size="small"
                                          color="primary"
                                          disabled={
                                            isEditDisabled ||
                                            !methods.getValues("patientId")?.value
                                          }
                                          onClick={() => {
                                            const currentPatient =
                                              methods.getValues("patientId");
                                            handleClickOpen(
                                              true,
                                              currentPatient?.label || "",
                                              "PATIENT",
                                            );
                                          }}
                                        >
                                          <Edit fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    )}
                                  />
                                </SkeletonWrapper>
                                <Typography className="validation-message">
                                  {methods.formState.errors.patientId?.message}
                                </Typography>
                              </FormControl>
                            </Grid>
                          )}
                          {/* <Grid
                            item
                            lg={6}
                            display={{
                              xs: "none",
                              lg: "grid",
                            }}
                          /> */}
                          <Grid item xs={12} sm={6} lg={3}>
                            {lastOpdCaseDate ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  height: "100%",
                                  pt: smd ? "" : "25px",
                                }}
                              >
                                <Typography className="fileds-label-text bold-600 primary-color ">
                                  Last visited on {lastOpdCaseDate}
                                  {(lastOpdDateDiffrence?.Year > 0 ||
                                    lastOpdDateDiffrence?.Month > 0 ||
                                    lastOpdDateDiffrence?.Day > 0) &&
                                    ", "}
                                  {lastOpdDateDiffrence?.Year > 0 &&
                                    `${lastOpdDateDiffrence.Year} year${
                                      lastOpdDateDiffrence.Year > 1 ? "s" : ""
                                    }`}
                                  {lastOpdDateDiffrence?.Year > 0 &&
                                    lastOpdDateDiffrence?.Month > 0 &&
                                    ", "}
                                  {lastOpdDateDiffrence?.Month > 0 &&
                                    `${lastOpdDateDiffrence.Month} month${
                                      lastOpdDateDiffrence.Month > 1 ? "s" : ""
                                    }`}
                                  {(lastOpdDateDiffrence?.Year > 0 ||
                                    lastOpdDateDiffrence?.Month > 0) &&
                                    lastOpdDateDiffrence?.Day > 0 &&
                                    ", "}
                                  {lastOpdDateDiffrence?.Day > 0 &&
                                    `${lastOpdDateDiffrence.Day} day${
                                      lastOpdDateDiffrence.Day > 1 ? "s" : ""
                                    }`}{" "}
                                  {(lastOpdDateDiffrence?.Year > 0 ||
                                    lastOpdDateDiffrence?.Month > 0 ||
                                    lastOpdDateDiffrence?.Day > 0) &&
                                    "ago"}
                                </Typography>
                              </Box>
                            ) : (
                              ""
                            )}
                          </Grid>
                          <Grid
                            item
                            lg={3}
                            display={{
                              xs: "none",
                              lg: "grid",
                            }}
                          />
                        </>
                      )}

                      {methods.getValues("appointmentTypeCode") ===
                        "MEDICAL_REPRESENTATIVE" && (
                        <>
                          <Grid item xs={12} sm={6} lg={3}>
                            <FormControl fullWidth>
                              <FormLabel
                                className={`fileds-label-text  ${
                                  methods.formState.errors.medicalRepresentativeId
                                    ? "validation-message"
                                    : ""
                                }`}
                              >
                                Medical Representative *
                              </FormLabel>
                              <SkeletonWrapper
                                isLoading={isLoading}
                                className="skeleton-input"
                              >
                                <Controller
                                  control={methods.control}
                                  name="medicalRepresentativeId"
                                  render={({ field }) => (
                                    <CustomAsyncSelect
                                      {...field}
                                      isNotCreatable={false}
                                      id={"medicalRepresentativeId"}
                                      FieldName={"medicalRepresentativeId"}
                                      defaultOptions={mrColl}
                                      openMenuOnFocus
                                      isClearable
                                      isSearchable
                                      placeholder="Search or create Medical Representative"
                                      menuPortalTarget={document.querySelector(
                                        "body",
                                      )}
                                      loadOptions={loadMedicalRepresentativeList}
                                      handleChange={async (e) => {
                                        if (e) {
                                          if (e.__isNew__ === true) {
                                            handleClickOpen(
                                              true,
                                              e.label,
                                              "MEDICAL_REPRESENTATIVE",
                                            );
                                          }
                                        }
                                        if (
                                          methods
                                            ?.getValues()
                                            ?.medicalRepresentativeId?.value?.toString() !==
                                          e?.value?.toString()
                                        ) {
                                          methods.setValue(
                                            "medicalRepresentativeId",
                                            e?.value ? e : null,
                                            {
                                              shouldValidate: true,
                                            },
                                          );
                                          methods.setValue(
                                            "medicalRepresentativeName",
                                            e?.value ? e?.label : null,
                                            {
                                              shouldValidate: true,
                                            },
                                          );
                                          let Mr: any;
                                          if (e?.value) {
                                            setIsLoading(true);
                                            Mr =
                                              await getMedicalRepresentativeDetailById(
                                                e.value,
                                              );
                                            setIsLoading(false);
                                          }
                                          handleMedicalRepresentativeDataChange(Mr);
                                        }
                                      }}
                                      className={`text-input ${
                                        methods.formState.errors.countryId
                                          ? "error"
                                          : ""
                                      }`}
                                    />
                                  )}
                                />
                              </SkeletonWrapper>
                              <Typography className="validation-message">
                                {
                                  methods.formState.errors.medicalRepresentativeId
                                    ?.message
                                }
                              </Typography>
                            </FormControl>
                          </Grid>
                          {/* <Grid item xs={12} sm={6} lg={3}>
                            <FormControl fullWidth>
                              <FormLabel
                                className={`fileds-label-text  ${
                                  methods.formState.errors.visitTypeCode
                                    ? "validation-message"
                                    : ""
                                }`}
                              >
                                Visit Type *
                              </FormLabel>
                              <SkeletonWrapper
                                isLoading={isLoading}
                                className="skeleton-input"
                              >
                                <Controller
                                  control={methods.control}
                                  name="visitTypeCode"
                                  render={({ field }) => (
                                    <CustomSelect
                                      {...field}
                                      isNotCreatable={true}
                                      id={"visitTypeCode"}
                                      FieldName={"visitTypeCode"}
                                      options={
                                        visitTypeOptionsMedicalRepresentative
                                      }
                                      openMenuOnFocus
                                      isClearable
                                      isSearchable
                                      menuPortalTarget={document.querySelector(
                                        "body"
                                      )}
                                      handleChange={(e) => {
                                        methods.setValue(
                                          "visitTypeCode",
                                          e?.value ? e : null,
                                          {
                                            shouldValidate: true,
                                          }
                                        );
                                        methods.setValue(
                                          "visitTypeCodeName",
                                          e?.value ? e?.label : null,
                                          {
                                            shouldValidate: true,
                                          }
                                        );
                                      }}
                                      className={`text-input ${
                                        methods.formState.errors.visitTypeCode
                                          ? "error"
                                          : ""
                                      }`}
                                    />
                                  )}
                                />
                              </SkeletonWrapper>
                              <Typography className="validation-message">
                                {methods.formState.errors.visitTypeCode?.message}
                              </Typography>
                            </FormControl>
                          </Grid>
                          <Grid
                            item
                            lg={6}
                            display={{
                              xs: "none",
                              lg: "grid",
                            }}
                          /> */}
                        </>
                      )}

                      {methods.getValues("appointmentTypeCode") === "PATIENT" && (
                        <>
                          {/* <Grid item xs={12}>
                            {methods?.getValues()?.patientId ? (
                              <Typography
                                sx={{
                                  fontWeight: 500,
                                  fontSize: "0.9rem",
                                  paddingTop: "8px",
                                }}
                              >
                                GENDER / AGE :{" "}
                                {methods?.getValues()?.gender?.label || "—"} /{" "}
                                {methods?.getValues()?.ageYear || "0"}Y /{" "}
                                {methods?.getValues()?.ageMonth || "0"}M /{" "}
                                {methods?.getValues()?.ageDay || "0"}D (
                                {methods?.getValues()?.dateOfBirth
                                  ? dayjs(methods.getValues().dateOfBirth).format(
                                      "DD-MM-YYYY"
                                    )
                                  : "—"}
                                )
                              </Typography>
                            ) : (
                              <Typography
                                sx={{
                                  fontWeight: 500,
                                  fontSize: "0.9rem",
                                  paddingTop: "8px",
                                }}
                              >
                                GENDER / AGE :
                              </Typography>
                            )}
                          </Grid> */}
                          <Grid item xs={12}>
                            {!showScheduledPatientCard && (
                              <Typography
                                sx={{
                                  fontWeight: 500,
                                  fontSize: "0.9rem",
                                  paddingTop: "8px",
                                }}
                              >
                                GENDER / AGE :{" "}
                                {hasPatientDetails ? (
                                  <>
                                    {methods?.getValues()?.gender?.label || "—"} /{" "}
                                    {(() => {
                                      const parts = [];
                                      const { ageYear, ageMonth, ageDay } =
                                        methods.getValues();

                                      if (ageYear > 0) parts.push(`${ageYear}Y`);
                                      if (ageMonth > 0) parts.push(`${ageMonth}M`);
                                      if (ageDay > 0) parts.push(`${ageDay}D`);

                                      return parts.length ? parts.join(" / ") : "—";
                                    })()}{" "}
                                    (
                                    {methods?.getValues()?.dateOfBirth
                                      ? dayjs(
                                          methods.getValues().dateOfBirth,
                                        ).format("DD-MM-YYYY")
                                      : "—"}
                                    )
                                  </>
                                ) : (
                                  "—"
                                )}
                              </Typography>
                            )}
                          </Grid>

                          {/* <Grid
                            item
                            lg={3}
                            display={{
                              xs: "none",
                              lg: "grid",
                            }}
                          ></Grid> */}

                          {/* <Grid item xs={12} sm={6} lg={3}>
                            <FormControl fullWidth>
                              <FormLabel
                                className={`fileds-label-text  ${methods.formState.errors.visitTypeCode
                                    ? "validation-message"
                                    : ""
                                  }`}
                              >
                                Visit type *
                              </FormLabel>
                              <SkeletonWrapper
                                isLoading={isLoading}
                                className="skeleton-input"
                              >
                                <Controller
                                  control={methods.control}
                                  name="visitTypeCode"
                                  render={({ field }) => (
                                    <CustomSelect
                                      {...field}
                                      isNotCreatable={true}
                                      id={"visitTypeCode"}
                                      FieldName={"visitTypeCode"}
                                      options={visitTypeOptions}
                                      openMenuOnFocus
                                      isClearable
                                      isSearchable
                                      menuPortalTarget={document.querySelector(
                                        "body"
                                      )}
                                      handleChange={(e) => {
                                        methods.setValue(
                                          "visitTypeCode",
                                          e?.value ? e : null,
                                          {
                                            shouldValidate: true,
                                          }
                                        );
                                        methods.setValue(
                                          "visitTypeCodeName",
                                          e?.value ? e?.label : null,
                                          {
                                            shouldValidate: true,
                                          }
                                        );
                                      }}
                                      className={`text-input ${methods.formState.errors.visitTypeCode
                                          ? "error"
                                          : ""
                                        }`}
                                    />
                                  )}
                                />
                              </SkeletonWrapper>
                              <Typography className="validation-message">
                                {methods.formState.errors.visitTypeCode?.message}
                              </Typography>
                            </FormControl>
                          </Grid> */}
                          <Grid item xs={12} sm={6} lg={3}>
                            <FormControl fullWidth>
                              <FormLabel
                                className={`fileds-label-text  ${
                                  methods.formState.errors.referredByCode
                                    ? "validation-message"
                                    : ""
                                }`}
                              >
                                Referred by
                              </FormLabel>
                              <SkeletonWrapper
                                isLoading={isLoading}
                                className="skeleton-input"
                              >
                                <Controller
                                  control={methods.control}
                                  name="referredByCode"
                                  render={({ field }) => (
                                    <CustomSelect
                                      {...field}
                                      isNotCreatable={true}
                                      id={"referredByCode"}
                                      FieldName={"referredByCode"}
                                      options={ReferredByOptions}
                                      openMenuOnFocus
                                      isClearable
                                      isSearchable
                                      menuPortalTarget={document.querySelector(
                                        "body",
                                      )}
                                      handleChange={(e) => {
                                        methods.setValue(
                                          "referredByCode",
                                          e?.value ? e : null,
                                          {
                                            shouldValidate: true,
                                          },
                                        );
                                        methods.setValue(
                                          "referredByCodeName",
                                          e?.value ? e?.label : null,
                                          {
                                            shouldValidate: true,
                                          },
                                        );
                                      }}
                                      className={`text-input ${
                                        methods.formState.errors.referredByCode
                                          ? "error"
                                          : ""
                                      }`}
                                    />
                                  )}
                                />
                              </SkeletonWrapper>
                              <Typography className="validation-message">
                                {methods.formState.errors.referredByCode?.message}
                              </Typography>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6} lg={3}>
                            {(methods.getValues("referredByCode")?.value ===
                              "ONLINE_SOCIAL_MEDIA" ||
                              methods.getValues("referredByCode")?.value ===
                                "MEDICAL_CAMP" ||
                              methods.getValues("referredByCode")?.value ===
                                "OTHER") && (
                              <>
                                <FormControl fullWidth>
                                  <FormLabel
                                    className={`fileds-label-text  ${
                                      methods.formState.errors.referredByName
                                        ? "validation-message"
                                        : ""
                                    }`}
                                  >
                                    Referred by{" "}
                                    {methods.getValues("referredByCode")?.label}{" "}
                                    Name
                                  </FormLabel>
                                  <SkeletonWrapper
                                    isLoading={isLoading}
                                    className="skeleton-input"
                                  >
                                    <CustomTextField
                                      placeholder=""
                                      autoCapitalize="none"
                                      type="text"
                                      disabled={
                                        parseInt(
                                          methods.getValues("departmentId")
                                            ?.value ?? "0",
                                        ) <= 0 ||
                                        parseInt(
                                          methods.getValues("specialityId")
                                            ?.value ?? "0",
                                        ) <= 0
                                      }
                                      fullWidth
                                      size="small"
                                      {...methods.register("referredByName")}
                                      error={
                                        !!methods.formState.errors.referredByName
                                      }
                                      className={`text-input ${
                                        methods.formState.errors.referredByName
                                          ? "error"
                                          : ""
                                      }`}
                                    />
                                  </SkeletonWrapper>
                                </FormControl>
                              </>
                            )}
                            {methods.getValues("referredByCode")?.value ===
                              "PATIENT" && (
                              <>
                                <FormControl fullWidth>
                                  <FormLabel
                                    className={`fileds-label-text  ${
                                      methods.formState.errors.referralPatientId
                                        ? "validation-message"
                                        : ""
                                    }`}
                                  >
                                    Referred by Patient Name
                                  </FormLabel>
                                  <SkeletonWrapper
                                    isLoading={isLoading}
                                    className="skeleton-input"
                                  >
                                    <Controller
                                      control={methods.control}
                                      name={"referralPatientId"}
                                      render={({ field }) => (
                                        <CustomAsyncSelect
                                          {...field}
                                          isNotCreatable={false}
                                          id={"referralPatientId"}
                                          FieldName={"referralPatientId"}
                                          placeholder="Search or create Patient"
                                          defaultOptions={
                                            filteredPatientColl(patientColl) || []
                                          }
                                          openMenuOnFocus
                                          isClearable
                                          isSearchable
                                          isDisabled={
                                            parseInt(
                                              methods.getValues("departmentId")
                                                ?.value ?? "0",
                                            ) <= 0 ||
                                            parseInt(
                                              methods.getValues("specialityId")
                                                ?.value ?? "0",
                                            ) <= 0
                                          }
                                          loadOptions={loadReferredPatientList}
                                          menuPortalTarget={document.querySelector(
                                            "body",
                                          )}
                                          handleChange={async (e: any) => {
                                            if (
                                              methods
                                                ?.getValues()
                                                ?.referralPatientId?.value?.toString() !==
                                              e?.value?.toString()
                                            ) {
                                              // UPDATE VALUE
                                              methods.setValue(
                                                "referralPatientId",
                                                e?.value ? e : null,
                                                {
                                                  shouldValidate: true,
                                                },
                                              );

                                              methods.setValue(
                                                "referralPatientName",
                                                e?.value ? e.label : null,
                                                {
                                                  shouldValidate: true,
                                                },
                                              );
                                            }
                                          }}
                                          onCreateOption={(inputvalue: string) => {
                                            handleClickOpen(
                                              true,
                                              inputvalue,
                                              "REFERRAL_PATIENT",
                                            );
                                          }}
                                          CustomOptionComponent={
                                            patientCollOptionComponent
                                          }
                                          className={`text-input ${
                                            methods.formState.errors
                                              .referralPatientId
                                              ? "error"
                                              : ""
                                          }`}
                                        />
                                      )}
                                    />
                                  </SkeletonWrapper>
                                </FormControl>
                              </>
                            )}
                            {methods.getValues("referredByCode")?.value ===
                              "REFERRAL_DOCTOR" && (
                              <>
                                <FormControl fullWidth>
                                  <FormLabel
                                    className={`fileds-label-text  ${
                                      methods.formState.errors.referralDoctorId
                                        ? "validation-message"
                                        : ""
                                    }`}
                                  >
                                    Referral Doctor
                                  </FormLabel>
                                  <SkeletonWrapper
                                    isLoading={isLoading}
                                    className="skeleton-input"
                                  >
                                    <Controller
                                      control={methods.control}
                                      name="referralDoctorId"
                                      render={({ field }) => (
                                        <CustomAsyncSelect
                                          {...field}
                                          isNotCreatable={false}
                                          id={"referralDoctorId"}
                                          FieldName={"referralDoctorId"}
                                          placeholder="Search or create Doctor"
                                          defaultOptions={refDoctorColl}
                                          openMenuOnFocus
                                          isClearable
                                          isSearchable
                                          isDisabled={
                                            parseInt(
                                              methods.getValues("departmentId")
                                                ?.value ?? "0",
                                            ) <= 0 ||
                                            parseInt(
                                              methods.getValues("specialityId")
                                                ?.value ?? "0",
                                            ) <= 0
                                          }
                                          loadOptions={(
                                            input: any,
                                            callback: any,
                                          ) => {
                                            loadRefDoctorList(
                                              input?.trim(),
                                              methods.getValues("departmentId")
                                                ?.value || "",
                                              methods.getValues("specialityId")
                                                ?.value || "",
                                              callback,
                                            );
                                          }}
                                          menuPortalTarget={document.querySelector(
                                            "body",
                                          )}
                                          handleChange={async (e: any) => {
                                            if (e) {
                                              if (e.__isNew__ === true) {
                                                handleClickOpen(
                                                  true,
                                                  e.label,
                                                  "REFERRAL_DOCTOR",
                                                );
                                              }
                                            }
                                            if (
                                              methods
                                                ?.getValues()
                                                ?.referralDoctorId?.value?.toString() !==
                                              e?.value?.toString()
                                            ) {
                                              methods.setValue(
                                                "referralDoctorId",
                                                e?.value ? e : null,
                                                {
                                                  shouldValidate: true,
                                                },
                                              );
                                              methods.setValue(
                                                "referralDoctorName",
                                                e?.value ? e?.label : null,
                                                {
                                                  shouldValidate: true,
                                                },
                                              );
                                            }
                                          }}
                                          className={`text-input ${
                                            methods.formState.errors
                                              .referralDoctorId
                                              ? "error"
                                              : ""
                                          }`}
                                        />
                                      )}
                                    />
                                  </SkeletonWrapper>
                                  <Typography className="validation-message">
                                    {
                                      methods.formState.errors.referralDoctorId
                                        ?.message
                                    }
                                  </Typography>
                                </FormControl>{" "}
                              </>
                            )}
                          </Grid>
                          <Grid
                            item
                            lg={6}
                            display={{
                              xs: "none",
                              lg: "grid",
                            }}
                          ></Grid>
                        </>
                      )}

                      {methods.getValues("appointmentTypeCode") ===
                        "MEDICAL_REPRESENTATIVE" && (
                        <Grid item xs={12} sm={6} lg={3}>
                          <FormControl fullWidth>
                            <FormLabel
                              className={`fileds-label-text  ${
                                methods.formState.errors.companyNameId
                                  ? "validation-message"
                                  : ""
                              }`}
                            >
                              Comapny Name *
                            </FormLabel>
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-input"
                            >
                              <Controller
                                control={methods.control}
                                name="companyNameId"
                                render={({ field }) => (
                                  <CustomAsyncSelect
                                    {...field}
                                    isNotCreatable={true}
                                    id={"companyNameId"}
                                    FieldName={"companyNameId"}
                                    defaultOptions={medicineCompanyColl}
                                    openMenuOnFocus
                                    isClearable
                                    isSearchable
                                    loadOptions={loadMedicineCompanyList}
                                    menuPortalTarget={document.querySelector(
                                      "body",
                                    )}
                                    handleChange={async (e) => {
                                      if (
                                        methods
                                          ?.getValues()
                                          ?.companyNameId?.value?.toString() !==
                                        e?.value?.toString()
                                      ) {
                                        methods.setValue(
                                          "companyNameId",
                                          e?.value ? e : null,
                                          {
                                            shouldValidate: true,
                                          },
                                        );
                                        methods.setValue(
                                          "companyName",
                                          e?.value ? e?.label : null,
                                          {
                                            shouldValidate: true,
                                          },
                                        );
                                      }
                                    }}
                                  />
                                )}
                              />
                            </SkeletonWrapper>
                            <Typography className="validation-message">
                              {methods.formState.errors.companyNameId?.message}
                            </Typography>
                          </FormControl>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>

                  <Grid item xs={12} className={"group-container"}>
                    <Grid item container spacing={2}>
                      {!isWalkIn && (
                        <>
                          <Grid item xs={12}>
                            <FormControl>
                              <FormLabel
                                className={`fileds-label-text ${
                                  methods.formState.errors.appointmentDate
                                    ? "validation-message"
                                    : ""
                                }`}
                              >
                                Appointment time *
                              </FormLabel>
                              <SkeletonWrapper
                                isLoading={isLoading}
                                className="skeleton-input"
                              >
                                {/* <Controller
                                  control={methods.control}
                                  name="appointmentDate"
                                  render={({ field }) => (
                                    <CustomDatePicker
                                      label=""
                                      {...field}
                                      value={
                                        methods.getValues().appointmentDate
                                          ? dayjs(
                                              methods.getValues().appointmentDate
                                            )
                                          : null
                                      }
                                      minDate={dayjs()}
                                      disabled={isWalkIn || isEditDisabled}
                                      shouldDisableDate={disableUnavailableDates}
                                      onDateChange={async (
                                        newTime: Dayjs | null
                                      ) => {
                                        methods.setValue(
                                          "appointmentDate",
                                          newTime ? newTime.toDate() : null,
                                          {
                                            shouldValidate: true,
                                          }
                                        );

                                        const locationId =
                                          methods.getValues()?.locationId?.value;
                                        if (locationId && newTime) {
                                          const dayName = newTime.format("dddd");
                                          const selectedDate =
                                            newTime.format("YYYY-MM-DD");
                                          await fetchAppointmentAvailability(
                                            locationId,
                                            selectedDate,
                                            dayName
                                          );
                                        }
                                      }}
                                      error={
                                        !!methods.formState.errors.appointmentDate
                                      }
                                    />
                                  )}
                                /> */}
                                <Controller
                                  control={methods.control}
                                  name="appointmentDate"
                                  rules={{
                                    validate: (value) => {
                                      if (!value) return true;

                                      const selected = dayjs(value).startOf("day");
                                      const today = dayjs().startOf("day");

                                      return selected.isBefore(today)
                                        ? "Not allowed to book appointment for past date"
                                        : true;
                                    },
                                  }}
                                  render={({ field }) => (
                                    <CustomDatePicker
                                      {...field}
                                      value={
                                        field.value ? dayjs(field.value) : null
                                      }
                                      minDate={dayjs()}
                                      disabled={isWalkIn || isEditDisabled}
                                      shouldDisableDate={disableUnavailableDates}
                                      onDateChange={async (newTime) => {
                                        methods.setValue(
                                          "appointmentDate",
                                          newTime ? newTime.toDate() : null,
                                          { shouldValidate: true },
                                        );

                                        if (
                                          !newTime ||
                                          dayjs(newTime)
                                            .startOf("day")
                                            .isBefore(dayjs().startOf("day"))
                                        ) {
                                          setAppointmentTimeSlots([]);
                                          methods.setValue("appointmentTime", null);
                                          return;
                                        }

                                        const locationId =
                                          methods.getValues()?.locationId?.value;
                                        const consultingDoctorId =
                                          methods.getValues()?.consultingDoctorId
                                            ?.value;
                                        if (locationId) {
                                          const dayName = newTime.format("dddd");
                                          const selectedDate =
                                            newTime.format("YYYY-MM-DD");

                                          await fetchAppointmentAvailability(
                                            locationId,
                                            selectedDate,
                                            dayName,
                                            consultingDoctorId,
                                          );
                                        }

                                        if (consultingDoctorId && curAppointmentId <= 0) {
                                          const appointmentNumber =
                                            await getAppointmentNumber(
                                              consultingDoctorId,
                                              newTime.toDate(),
                                            );
                                          methods.setValue(
                                            "appointmentNumber",
                                            appointmentNumber?.autoNumber || 0,
                                            { shouldValidate: true },
                                          );
                                          methods.setValue(
                                            "slotNumber",
                                            appointmentNumber?.slotNumber || 0,
                                            { shouldValidate: true },
                                          );
                                        }
                                      }}
                                      error={
                                        !!methods.formState.errors.appointmentDate
                                      }
                                    />
                                  )}
                                />
                              </SkeletonWrapper>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12}>
                            {Object.entries(groupedSlots).length > 0 ? (
                              <Grid item container spacing={2} mt={1}>
                                {Object.entries(groupedSlots).map(
                                  ([day, sessions]: any) => (
                                    <Grid item xs={12} key={day}>
                                      <strong style={{ fontSize: "15px" }}>
                                        {day}
                                      </strong>

                                      {Object.entries(sessions).map(
                                        ([sessionName, slots]: any) => (
                                          <Grid
                                            item
                                            container
                                            spacing={1}
                                            key={sessionName}
                                          >
                                            <Grid item xs={12}>
                                              <em
                                                style={{
                                                  fontSize: "14px",
                                                  color: "#555",
                                                }}
                                              >
                                                {sessionName}
                                              </em>
                                            </Grid>
                                            <Grid item xs={12}>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  flexWrap: "wrap",
                                                  gap: 1,
                                                }}
                                              >
                                                {slots.map(
                                                  (slot: any, i: number) => {
                                                    const timeString =
                                                      slot.start.format("HH:mm");
                                                    const selectedTime =
                                                      methods.getValues()
                                                        .appointmentTime;
                                                    return (
                                                      <Button
                                                        key={i}
                                                        variant="outlined"
                                                        sx={{
                                                          minWidth: "60px",
                                                          maxWidth: "60px",
                                                        }}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          if (
                                                            selectedTime ===
                                                            timeString
                                                          ) {
                                                            methods.setValue(
                                                              "appointmentTime",
                                                              null,
                                                              {
                                                                shouldValidate: true,
                                                              },
                                                            );
                                                          } else {
                                                            methods.setValue(
                                                              "appointmentTime",
                                                              timeString,
                                                              {
                                                                shouldValidate: true,
                                                              },
                                                            );
                                                          }
                                                        }}
                                                        sx={{
                                                          backgroundColor:
                                                            selectedTime ===
                                                            timeString
                                                              ? "#414d55"
                                                              : "transparent",
                                                          color:
                                                            selectedTime ===
                                                            timeString
                                                              ? "#fff"
                                                              : "#285cdb",
                                                          "&:hover": {
                                                            backgroundColor:
                                                              "#414d55",
                                                            color: "#fff",
                                                          },
                                                        }}
                                                      >
                                                        {timeString}
                                                      </Button>
                                                    );
                                                  },
                                                )}
                                              </Box>
                                            </Grid>
                                          </Grid>
                                        ),
                                      )}
                                    </Grid>
                                  ),
                                )}
                              </Grid>
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "text.secondary",
                                  py: 2,
                                }}
                              >
                                No available time slots for this date
                              </Typography>
                            )}
                          </Grid>
                        </>
                      )}

                      <Grid item xs={12} md={6} lg={6}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.reasonForVisitColl
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Reason for Visit *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name={`reasonForVisitColl`}
                              render={({ field }) => (
                                <CustomAsyncSelect
                                  {...field}
                                  id={`reasonForVisitColl`}
                                  FieldName={`reasonForVisitColl`}
                                  isMulti
                                  placeholder="Search or create Reason for Visit"
                                  defaultOptions={itemColl}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  hideSelectedOptions
                                  inputValue={inputValue}
                                  onSelectResetsInput={true}
                                  closeMenuOnSelect={false}
                                  loadOptions={loadItemList}
                                  menuPortalTarget={document.querySelector("body")}
                                  handleChange={(e) => {
                                    setInputValue("");
                                    const visitColl =
                                      e?.map((item: any, index: number) => {
                                        return {
                                          ...item,
                                          displayOrder: index,
                                        };
                                      }) || [];
                                    const previous =
                                      methods.getValues("reasonForVisitColl") || [];
                                    const getReasonKey = (item: any) =>
                                      item?.appointmentReasonForVisitId ??
                                      item?.value ??
                                      item?.itemId ??
                                      item?.label;
                                    const nextKeys = new Set(
                                      visitColl
                                        .map((item: any) => getReasonKey(item))
                                        .filter(
                                          (key: any) =>
                                            key !== undefined && key !== null,
                                        ),
                                    );
                                    const removedExisting = previous.filter(
                                      (item: any) => {
                                        const key = getReasonKey(item);
                                        return (
                                          key !== undefined &&
                                          !nextKeys.has(key) &&
                                          item?.appointmentReasonForVisitId
                                        );
                                      },
                                    );
                                    setDeletedReasonForVisit((current) => {
                                      const currentByKey = new Map(
                                        current.map((item: any) => [
                                          String(getReasonKey(item)),
                                          item,
                                        ]),
                                      );
                                      nextKeys.forEach((key: any) => {
                                        currentByKey.delete(String(key));
                                      });
                                      removedExisting.forEach((item: any) => {
                                        const key = String(getReasonKey(item));
                                        if (!currentByKey.has(key)) {
                                          currentByKey.set(key, {
                                            ...item,
                                            isDeleted: 1,
                                            mode: "D",
                                          });
                                        }
                                      });
                                      return Array.from(currentByKey.values());
                                    });
                                    methods.setValue(
                                      "reasonForVisitColl",
                                      visitColl,
                                      {
                                        shouldValidate: true,
                                      },
                                    );
                                  }}
                                  onCreateOption={(inputvalue) => {
                                    handleClickOpen(true, inputvalue, "ITEM");
                                  }}
                                  onInputChange={(newValue, actionMeta) => {
                                    if (actionMeta.action === "input-change") {
                                      setInputValue(newValue);
                                      return newValue;
                                    }
                                    if (
                                      actionMeta.action === "set-value" ||
                                      actionMeta.action === "menu-close"
                                    ) {
                                      setInputValue("");
                                      return "";
                                    }
                                    return "";
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.reasonForVisitColl?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>

                  {!isWalkIn && !isCalendar && (
                    <Grid item xs={12} className={"group-container"}>
                      <Typography
                        className="font-size-16 text-link"
                        onClick={(e) => {
                          setSeeMoreVitals(!seeMoreVitals);
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                          }}
                        >
                          <Typography>Vitals and Anthropometry</Typography>
                          {seeMoreVitals ? <ExpandLess /> : <ExpandMore />}
                        </Box>
                      </Typography>
                    </Grid>
                  )}
                  {(seeMoreVitals || isWalkIn) && (
                    <>
                      {methods.getValues("appointmentTypeCode") === "PATIENT" && (
                        <Grid item xs={12} className="group-container">
                          <Grid container item spacing={2}>
                            <Grid item xs={12}>
                              <Typography variant="h6">
                                Vitals and Anthropometry
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.weight
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  Weight{" "}
                                  <CustomTooltip
                                    title="This field is important for automated dose calculation"
                                    hideHeader={true}
                                  ></CustomTooltip>
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <CustomTextField
                                    placeholder="000.000"
                                    fullWidth
                                    id="weight"
                                    size="small"
                                    disabled={isEditDisabled}
                                    inputProps={{
                                      ...weightProps,
                                    }}
                                    {...methods.register("weight")}
                                    onFocus={handleFocus}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          Kg
                                        </InputAdornment>
                                      ),
                                    }}
                                    error={!!methods.formState.errors.weight}
                                  />
                                </SkeletonWrapper>
                                <Typography className="validation-message">
                                  {methods.formState.errors.weight?.message}
                                </Typography>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.height
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  Height/Length{" "}
                                  <CustomTooltip
                                    title="This field is important for automated dose calculation"
                                    hideHeader={true}
                                  ></CustomTooltip>
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <CustomTextField
                                    placeholder="000.0"
                                    fullWidth
                                    id="height"
                                    size="small"
                                    disabled={isEditDisabled}
                                    inputProps={{
                                      ...heightProps,
                                    }}
                                    {...methods.register("height")}
                                    onFocus={handleFocus}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          cm
                                        </InputAdornment>
                                      ),
                                    }}
                                    error={!!methods.formState.errors.height}
                                  />
                                </SkeletonWrapper>
                                <Typography className="validation-message">
                                  {methods.formState.errors.height?.message}
                                </Typography>
                              </FormControl>
                            </Grid>

                            {/* HEAD CIRCUMFERENCE */}
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.headCircumference
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  HC
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <CustomTextField
                                    placeholder="000.0"
                                    fullWidth
                                    id="headCircumference"
                                    size="small"
                                    disabled={isEditDisabled}
                                    inputProps={{
                                      ...headCircumferenceProps,
                                    }}
                                    {...methods.register("headCircumference")}
                                    onFocus={handleFocus}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          cm
                                        </InputAdornment>
                                      ),
                                    }}
                                    error={
                                      !!methods.formState.errors.headCircumference
                                    }
                                  />
                                </SkeletonWrapper>
                                <Typography className="validation-message">
                                  {
                                    methods.formState.errors.headCircumference
                                      ?.message
                                  }
                                </Typography>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.pr
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  Pulse Rate
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <CustomTextField
                                    placeholder="0"
                                    fullWidth
                                    id="pr"
                                    size="small"
                                    disabled={isEditDisabled}
                                    inputProps={{
                                      ...prProps,
                                    }}
                                    {...methods.register("pr")}
                                    onFocus={handleFocus}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          bpm
                                        </InputAdornment>
                                      ),
                                    }}
                                    error={!!methods.formState.errors.pr}
                                  />
                                </SkeletonWrapper>
                                <Typography className="validation-message">
                                  {methods.formState.errors.pr?.message}
                                </Typography>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.respiratoryRate
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  Respiratory Rate
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <CustomTextField
                                    placeholder="0"
                                    fullWidth
                                    id="respiratoryRate"
                                    size="small"
                                    disabled={isEditDisabled}
                                    inputProps={{
                                      ...rrProps,
                                    }}
                                    {...methods.register("respiratoryRate")}
                                    onFocus={handleFocus}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          /min
                                        </InputAdornment>
                                      ),
                                    }}
                                    error={
                                      !!methods.formState.errors.respiratoryRate
                                    }
                                  />
                                </SkeletonWrapper>
                                <Typography className="validation-message">
                                  {
                                    methods.formState.errors.respiratoryRate
                                      ?.message
                                  }
                                </Typography>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={8} md={6} lg={4} xl={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.spO2Amount
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  SPO2 with Site of Measurement
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <CustomTextField
                                    placeholder="0"
                                    fullWidth
                                    id="spO2Amount"
                                    size="small"
                                    disabled={isEditDisabled}
                                    inputProps={{
                                      ...spo2Props,
                                    }}
                                    onFocus={handleFocus}
                                    {...methods.register("spO2Amount", {
                                      onChange(event) {
                                        methods.setValue(
                                          "spO2Amount",
                                          event.target.value,
                                          {
                                            shouldValidate: true,
                                          },
                                        );
                                      },
                                    })}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                            }}
                                          >
                                            <Typography sx={{ mr: 1 }}>
                                              %
                                            </Typography>
                                            <Box className="input-end-andorment-border">
                                              <Select
                                                value={
                                                  methods.getValues().spO2SiteUnit
                                                }
                                                onChange={(e) => {
                                                  methods.setValue(
                                                    "spO2SiteUnit",
                                                    e.target.value,
                                                    { shouldValidate: true },
                                                  );
                                                }}
                                                size="small"
                                                disabled={isEditDisabled}
                                                displayEmpty
                                                inputProps={{
                                                  "aria-label": "Without label",
                                                }}
                                                sx={{
                                                  ".MuiOutlinedInput-notchedOutline":
                                                    {
                                                      border: "none",
                                                    },
                                                  ".MuiSelect-select": {
                                                    paddingRight: "0px",
                                                  },
                                                }}
                                              >
                                                {SpO2MesurementDefaultOptions?.map(
                                                  (x: any, index: number) => (
                                                    <MenuItem
                                                      key={index?.toString()}
                                                      value={x.value}
                                                    >
                                                      {x.label}
                                                    </MenuItem>
                                                  ),
                                                )}
                                              </Select>
                                            </Box>
                                          </Box>
                                        </InputAdornment>
                                      ),
                                    }}
                                    sx={{
                                      minWidth: "150px",
                                      ".MuiOutlinedInput-root": {
                                        paddingRight: 0,
                                      },
                                    }}
                                    error={!!methods.formState.errors.spO2Amount}
                                  />
                                </SkeletonWrapper>
                              </FormControl>
                              <Typography className="validation-message">
                                {methods.formState.errors.spO2Amount?.message}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={8} md={6} lg={4} xl={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.bpLeftAmount
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  BP with site of Measurement
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <CustomTextField
                                    placeholder="000/000"
                                    fullWidth
                                    id="bpLeftAmount"
                                    size="small"
                                    disabled={isEditDisabled}
                                    inputProps={bpProps}
                                    onFocus={handleFocus}
                                    {...methods.register("bpDisplayValue", {
                                      onChange(event) {
                                        let inputvalue = event.target.value;
                                        const [before, after] =
                                          inputvalue?.split("/");
                                        methods.setValue(
                                          "bpLeftAmount",
                                          before ? parseInt(before, 10) : null,
                                          {
                                            shouldValidate: true,
                                          },
                                        );
                                        methods.setValue(
                                          "bpRightAmount",
                                          after ? parseInt(after, 10) : null,
                                          {
                                            shouldValidate: true,
                                          },
                                        );
                                        methods.setValue(
                                          "bpDisplayValue",
                                          inputvalue || null,
                                          {
                                            shouldValidate: true,
                                          },
                                        );
                                      },
                                    })}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                            }}
                                          >
                                            <Typography sx={{ mr: 1 }}>
                                              mmHg
                                            </Typography>
                                            <Box className="input-end-andorment-border">
                                              <Select
                                                value={
                                                  methods.getValues().bpSiteUnit
                                                }
                                                onChange={(e) => {
                                                  methods.setValue(
                                                    "bpSiteUnit",
                                                    e.target.value,
                                                    { shouldValidate: true },
                                                  );
                                                }}
                                                size="small"
                                                disabled={isEditDisabled}
                                                displayEmpty
                                                inputProps={{
                                                  "aria-label": "Without label",
                                                }}
                                                sx={{
                                                  ".MuiOutlinedInput-notchedOutline":
                                                    {
                                                      border: "none",
                                                    },
                                                  ".MuiSelect-select": {
                                                    paddingRight: "0px",
                                                  },
                                                }}
                                              >
                                                {BPMesurementDefaultOptions?.map(
                                                  (x: any, index: number) => (
                                                    <MenuItem
                                                      key={index?.toString()}
                                                      value={x.value}
                                                    >
                                                      {x.label}
                                                    </MenuItem>
                                                  ),
                                                )}
                                              </Select>
                                            </Box>
                                          </Box>
                                        </InputAdornment>
                                      ),
                                    }}
                                    sx={{
                                      minWidth: "150px",
                                      ".MuiOutlinedInput-root": {
                                        paddingRight: 0,
                                      },
                                    }}
                                    error={!!methods.formState.errors.bpLeftAmount}
                                  />
                                </SkeletonWrapper>
                              </FormControl>
                              <Typography className="validation-message">
                                {methods.formState.errors.bpLeftAmount?.message}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} xl={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.temperatureAmount
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  Temperature
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <CustomTextField
                                    placeholder="000.0"
                                    fullWidth
                                    id="temperatureAmount"
                                    size="small"
                                    disabled={isEditDisabled}
                                    inputProps={tempetureProps}
                                    onFocus={handleFocus}
                                    {...methods.register("temperatureAmount", {
                                      onChange(event) {
                                        methods.setValue(
                                          "temperatureAmount",
                                          event.target.value,
                                          {
                                            shouldValidate: true,
                                          },
                                        );
                                      },
                                    })}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <Box
                                            sx={{
                                              display: "flex",
                                            }}
                                          >
                                            <Box className="input-end-andorment-border">
                                              <Select
                                                value={
                                                  methods.getValues()
                                                    .temperatureUnit
                                                }
                                                onChange={(e) => {
                                                  methods.setValue(
                                                    "temperatureUnit",
                                                    e.target.value,
                                                    { shouldValidate: true },
                                                  );
                                                }}
                                                size="small"
                                                displayEmpty
                                                inputProps={{
                                                  "aria-label": "Without label",
                                                }}
                                                sx={{
                                                  ".MuiOutlinedInput-notchedOutline":
                                                    {
                                                      border: "none",
                                                    },
                                                  ".MuiSelect-select": {
                                                    paddingRight: "0px",
                                                  },
                                                }}
                                              >
                                                {temperatureOptions?.map(
                                                  (x: any, index: number) => (
                                                    <MenuItem
                                                      key={index?.toString()}
                                                      value={x.value}
                                                    >
                                                      {x.label}
                                                    </MenuItem>
                                                  ),
                                                )}
                                              </Select>
                                            </Box>
                                          </Box>
                                        </InputAdornment>
                                      ),
                                    }}
                                    sx={{
                                      minWidth: "150px",
                                      ".MuiOutlinedInput-root": {
                                        paddingRight: 0,
                                      },
                                    }}
                                    error={
                                      !!methods.formState.errors.temperatureAmount
                                    }
                                  />
                                </SkeletonWrapper>
                              </FormControl>
                              <Typography className="validation-message">
                                {
                                  methods.formState.errors.temperatureAmount
                                    ?.message
                                }
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} xl={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.temperatureByPalpation
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  Temperature by Palpation
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <Controller
                                    control={methods.control}
                                    name="temperatureByPalpation"
                                    render={({ field }) => (
                                      <CustomSelect
                                        {...field}
                                        isNotCreatable={true}
                                        id={"temperatureByPalpation"}
                                        FieldName={"temperatureByPalpation"}
                                        options={temperatureByPalpationOptions}
                                        openMenuOnFocus
                                        isDisabled={isEditDisabled}
                                        isClearable
                                        isSearchable
                                        menuPortalTarget={document.querySelector(
                                          "body",
                                        )}
                                        handleChange={(e) => {
                                          methods.setValue(
                                            "temperatureByPalpation",
                                            e !== null ? e : null,
                                            { shouldValidate: true },
                                          );
                                        }}
                                        className={`text-input ${
                                          methods.formState.errors
                                            .temperatureByPalpation
                                            ? "error"
                                            : ""
                                        }`}
                                      />
                                    )}
                                  />
                                </SkeletonWrapper>
                                <Typography className="validation-message">
                                  {
                                    methods.formState.errors.temperatureByPalpation
                                      ?.message
                                  }
                                </Typography>
                              </FormControl>
                            </Grid>

                            {/* RBS */}
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.rbs
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  RBS
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <CustomTextField
                                    placeholder="0"
                                    fullWidth
                                    id="rbs"
                                    size="small"
                                    disabled={isEditDisabled}
                                    inputProps={{
                                      ...prProps,
                                    }}
                                    {...methods.register("rbs")}
                                    onFocus={handleFocus}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          mg/dl
                                        </InputAdornment>
                                      ),
                                    }}
                                    error={!!methods.formState.errors.rbs}
                                  />
                                </SkeletonWrapper>
                                <Typography className="validation-message">
                                  {methods.formState.errors.rbs?.message}
                                </Typography>
                              </FormControl>
                            </Grid>

                            {/* MOTHER'S HEIGHT */}
                            {/* <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${methods.formState.errors.motherHeight
                                    ? "validation-message"
                                    : ""
                                    }`}
                                >
                                  Mother's Height{" "}
                                  <CustomTooltip
                                    title="This field is important for automated dose calculation"
                                    hideHeader={true}
                                  ></CustomTooltip>
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <CustomTextField
                                    placeholder="000.0"
                                    fullWidth
                                    id="motherHeight"
                                    size="small"
                                    inputProps={{
                                      ...heightProps,
                                    }}
                                    {...methods.register("motherHeight")}
                                    onFocus={handleFocus}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          cm
                                        </InputAdornment>
                                      ),
                                    }}
                                    error={!!methods.formState.errors.motherHeight}
                                  />
                                </SkeletonWrapper>
                                <Typography className="validation-message">
                                  {methods.formState.errors.motherHeight?.message}
                                </Typography>
                              </FormControl>
                            </Grid> */}

                            {/* FATHER'S HEIGHT */}
                            {/* <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${methods.formState.errors.fatherHeight
                                    ? "validation-message"
                                    : ""
                                    }`}
                                >
                                  Father's Height{" "}
                                  <CustomTooltip
                                    title="This field is important for automated dose calculation"
                                    hideHeader={true}
                                  ></CustomTooltip>
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <CustomTextField
                                    placeholder="000.0"
                                    fullWidth
                                    id="fatherHeight"
                                    size="small"
                                    inputProps={{
                                      ...heightProps,
                                    }}
                                    {...methods.register("fatherHeight")}
                                    onFocus={handleFocus}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          cm
                                        </InputAdornment>
                                      ),
                                    }}
                                    error={!!methods.formState.errors.fatherHeight}
                                  />
                                </SkeletonWrapper>
                                <Typography className="validation-message">
                                  {methods.formState.errors.fatherHeight?.message}
                                </Typography>
                              </FormControl>
                            </Grid> */}
                          </Grid>
                        </Grid>
                      )}
                    </>
                  )}
                  {!isCalendar && !showScheduledPatientCard && (
                    <Grid item xs={12} className={"group-container"}>
                      <Typography
                        className="font-size-16 text-link"
                        onClick={(e) => {
                          setSeeMore(!seeMore);
                        }}
                      >
                        {seeMore ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Typography>See Less</Typography>
                            <ExpandLess />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Typography>See More</Typography>
                            <ExpandMore />
                          </Box>
                        )}
                      </Typography>
                    </Grid>
                  )}
                </>
              )}

              {param.tabType === 1 && (
                <>
                  <Grid item xs={12} className={"group-container"}>
                     <Grid item xs={8} sm={8} md={8} lg={10} mb={2}>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-search-box"
                        >
                          <TextField
                            id="searchBox"
                            placeholder="Search..."
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchValue(value);
                              }
                            }}
                            type="text"
                            variant="outlined"
                            size="small"
                            sx={{
                              minWidth: 235,
                            }}
                            onChange={(e) => setValue(e.target.value)}
                            value={value}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      searchValue(value);
                                    }}
                                  >
                                    {" "}
                                    <SearchIcon />
                                  </IconButton>
                                </InputAdornment>
                              ),
                              endAdornment: value && (
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() => {
                                    searchValue("");
                                    setValue("");
                                  }}
                                >
                                  <ClearIcon />
                                </IconButton>
                              ),
                            }}
                          />
                        </SkeletonWrapper>
                      </Grid>
                    <TableContainer
                      component={Paper}
                      sx={{
                        maxHeight: "600px",
                        overflowY: "auto",
                      }}
                    >
                      <Table size="small">
                        <EnhancedTableHead
                          numSelected={resultData?.appointmentReportParameterColl?.length || 0}
                          order={order}
                          orderBy={orderBy}
                          onSelectAllClick={handleCheckSelectAll}
                          rowCount={resultData?.appointmentReportParameterColl?.length}
                        />
                        <TableBody>
                          {showSkeleton ? (
                            Array(skeletonRowCount)
                              ?.fill(0)
                              ?.map((skeletonRow, index) => {
                                return (
                                  <TableRow key={"skeleton-row" + index?.toString()}>
                                    <TableCell colSpan={5}>
                                      <SkeletonWrapper
                                        isLoading={showSkeleton}
                                        className="skeleton-input"
                                      >
                                        <span></span>
                                      </SkeletonWrapper>
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                          ) : (
                            <>
                              {stableSort(
                                resultData?.appointmentReportParameterColl,
                                getComparator(order, orderBy),
                              ).map((item: any, index: any) => {
                                return (
                                  <TableRow key={index}>
                                    <TableCell colSpan={5} sx={{ p: 1 }}>
                                      <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={2.5} md={2.4}>
                                          <Typography
                                            sx={{
                                              display: { xs: "block", sm: "none" },
                                              fontWeight: 600,
                                            }}
                                          >
                                            Investigation
                                          </Typography>
                                          {item.investigationName}
                                        </Grid>
                                        <Grid item xs={12} sm={2.5} md={2.4}>
                                          <Typography
                                            sx={{
                                              display: { xs: "block", sm: "none" },
                                              fontWeight: 600,
                                            }}
                                          >
                                            Parameter
                                          </Typography>
                                          {item.parameterName}
                                        </Grid>
                                        <Grid item xs={12} sm={2} md={2}>
                                          <Typography
                                            sx={{
                                              display: { xs: "block", sm: "none" },
                                              fontWeight: 600,
                                            }}
                                          >
                                            Specimen
                                          </Typography>
                                          {item.specimen}
                                        </Grid>
                                        <Grid item xs={12} sm={3} md={2.2}>
                                          <Typography
                                            sx={{
                                              display: { xs: "block", sm: "none" },
                                              fontWeight: 600,
                                            }}
                                          >
                                            Value
                                          </Typography>
                                          {(() => {
                                            const isQualitative =
                                              item?.parameterType?.toString().toLowerCase() === "qualitative";
                                            if (isQualitative) {
                                              const options = investigationParameterRangeColl.filter(
                                                (r: any) =>
                                                  Number(r?.investigationParameterId) ===
                                                  Number(item?.investigationParameterId),
                                              );
                                              return (
                                              <Controller
                                                control={methods.control}
                                                name={`reportParameterColl.${index}.optionValue`}
                                                render={({ field }) => (
                                                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                    {options.length > 0 &&
                                                      options.map((opt: any) => {
                                                        const optionValue = opt?.optionValue;
                                                        const isSelected = field.value === optionValue;
                                                        return (
                                                          <Box
                                                            key={opt?.memberPatientReportParameterRangeValueId}
                                                            className="quick-chip"
                                                            onClick={() => field.onChange(isSelected ? "" : optionValue)}
                                                            sx={{
                                                              cursor: "pointer",
                                                              padding: "6px 10px !important",
                                                              ...(isSelected && {
                                                                "&&": {
                                                                  backgroundColor: "primary.main",
                                                                  color: "#fff",
                                                                  borderColor: "primary.main",
                                                                },
                                                              }),
                                                            }}
                                                          >
                                                            {optionValue}
                                                          </Box>
                                                        );
                                                      })}
                                                  </Box>
                                                )}
                                              />
                                            )}
                                            return (
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  border: "1px solid",
                                                  borderColor: "divider",
                                                  borderRadius: 1,
                                                  overflow: "hidden",
                                                  maxWidth: "100%",
                                                }}
                                              >
                                                <Controller
                                                  control={methods.control}
                                                  name={`reportParameterColl.${index}.unitValue`}
                                                  render={({ field }) => (
                                                    <CustomTextField
                                                      {...field}
                                                      placeholder="000.0"
                                                      fullWidth
                                                      id="unitValue"
                                                      variant="standard"
                                                      InputProps={{
                                                        disableUnderline: true,
                                                      }}
                                                      sx={{
                                                        paddingRight: "5px",
                                                        borderRight: "1px solid #e0e0e0",
                                                        "& .MuiInputBase-input": {
                                                          textAlign: "right",
                                                          fontSize: "18px",
                                                          fontWeight: 600,
                                                        },
                                                        "& .MuiInputBase-input::placeholder":
                                                          {
                                                            fontSize: "16px",
                                                            fontWeight: 400,
                                                          },
                                                      }}
                                                    />
                                                  )}
                                                />
                                                <Controller
                                                  control={methods.control}
                                                  name={`reportParameterColl.${index}.unitName`}
                                                  render={({ field }) => {
                                                    const parameterUnits = unitColl.filter(
                                                      (x: any) =>
                                                        x?.investigationParameterId === item?.investigationParameterId,
                                                    );
                                                    const defaultUnit =
                                                      parameterUnits?.[0]?.unitName || "";
                                                    const selectedValue = field.value || defaultUnit;
                                                    return (
                                                      <Select
                                                        {...field}
                                                        value={selectedValue}
                                                        onChange={(e) => {
                                                          const selectedValue = e.target.value;

                                                          const selectedUnit = parameterUnits.find(
                                                            (x: any) => x.unitName === selectedValue,
                                                          );
                                                          field.onChange(selectedValue);
                                                          methods.setValue(
                                                            `reportParameterColl.${index}.investigationParameterUnitId`,
                                                            selectedUnit?.investigationParameterUnitId ?? 0,
                                                          );
                                                          methods.setValue(
                                                            `reportParameterColl.${index}.unitName`,
                                                            selectedValue,
                                                          );
                                                        }}
                                                        variant="standard"
                                                        size="small"
                                                        disableUnderline
                                                        MenuProps={{
                                                          PaperProps: {
                                                            sx: {
                                                              border: "1px solid #e0e0e0",
                                                              borderRadius: 1,
                                                            },
                                                          },
                                                        }}
                                                        sx={{
                                                          px: 1,
                                                          borderColor: "divider",
                                                          minWidth: 120,
                                                        }}
                                                      >
                                                        {parameterUnits?.map((unit: any, unitIndex: number) => (
                                                          <MenuItem
                                                            key={`${unit?.investigationParameterId}-${unitIndex}`}
                                                            value={unit?.unitName}
                                                          >
                                                            {unit?.unitName}
                                                          </MenuItem>
                                                        ))}
                                                      </Select>
                                                    );
                                                  }}
                                                />
                                              </Box>
                                            )
                                          })()}
                                        </Grid>
                                        <Grid item xs={12} sm={2} md={3}>
                                          <>
                                            <Typography
                                              sx={{
                                                display: { xs: "block", sm: "none" },
                                                fontWeight: 600,
                                              }}
                                            >
                                              Remarks
                                            </Typography>
                                              <Controller
                                                control={methods.control}
                                                name={`reportParameterColl.${index}.remarks`}
                                                render={({ field }) => (
                                                  <CustomTextField
                                                    {...field}
                                                    placeholder=""
                                                    fullWidth
                                                    size="small"
                                                  />
                                                )}
                                              />
                                          </>
                                        </Grid>
                                      </Grid>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                              {resultData?.appointmentReportParameterColl?.length <= 0 && (
                                <TableRow>
                                  <TableCell align="center" colSpan={5}>
                                    No record(s) found
                                  </TableCell>
                                </TableRow>
                              )}
                            </>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </>
              )}

              {seeMore && !showScheduledPatientCard && (
                <>
                  <Grid item xs={12} className={"group-container"}>
                    <Grid item container spacing={2}>
                      <Grid item xs={12}>
                        {methods.getValues("appointmentTypeCode") ===
                          "PATIENT" && (
                          <Typography variant="h6">
                            Patient Information
                          </Typography>
                        )}
                        {methods.getValues("appointmentTypeCode") ===
                          "MEDICAL_REPRESENTATIVE" && (
                          <Typography variant="h6">
                            Medical Representative Information
                          </Typography>
                        )}
                      </Grid>
                      {methods.getValues("appointmentTypeCode") ===
                        "PATIENT" && (
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth>
                            <FormLabel className={`fileds-label-text `}>
                              EMR ID *
                            </FormLabel>
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-input"
                            >
                              <CustomTextField
                                placeholder=""
                                autoCapitalize="none"
                                disabled
                                type="text"
                                fullWidth
                                size="small"
                                value={methods.getValues("emrId")}
                                error={!!methods.formState.errors.emrId}
                                className={`text-input `}
                              />
                            </SkeletonWrapper>
                          </FormControl>
                        </Grid>
                      )}

                      {methods.getValues("appointmentTypeCode") ===
                        "MEDICAL_REPRESENTATIVE" && (
                        <>
                          <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth>
                              <FormLabel className={`fileds-label-text `}>
                                Medical Representative ID
                              </FormLabel>
                              <SkeletonWrapper
                                isLoading={isLoading}
                                className="skeleton-input"
                              >
                                <CustomTextField
                                  placeholder=""
                                  autoCapitalize="none"
                                  type="text"
                                  disabled
                                  fullWidth
                                  size="small"
                                  value={
                                    methods.getValues("medicalRepresentativeId")
                                      ?.value
                                  }
                                  error={
                                    !!methods.formState.errors
                                      .medicalRepresentativeId
                                  }
                                  className={`text-input ${
                                    methods.formState.errors
                                      .medicalRepresentativeId
                                      ? "error"
                                      : ""
                                  }`}
                                />
                              </SkeletonWrapper>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth>
                              <FormLabel
                                className={`fileds-label-text  ${
                                  methods.formState.errors.prefix
                                    ? "validation-message"
                                    : ""
                                }`}
                              >
                                Prefix *
                              </FormLabel>
                              <SkeletonWrapper
                                isLoading={isLoading}
                                className="skeleton-input"
                              >
                                <Controller
                                  control={methods.control}
                                  name="prefix"
                                  render={({ field }) => (
                                    <CustomSelect
                                      {...field}
                                      isNotCreatable={true}
                                      id={"prefix"}
                                      FieldName={"prefix"}
                                      options={prefixOptions}
                                      openMenuOnFocus
                                      isClearable
                                      isDisabled
                                      isSearchable
                                      menuPortalTarget={document.querySelector(
                                        "body",
                                      )}
                                      handleChange={async (e) => {
                                        if (
                                          methods
                                            ?.getValues()
                                            ?.prefix?.value?.toString() !==
                                          e?.value?.toString()
                                        ) {
                                          methods.setValue(
                                            "prefix",
                                            e?.value ? e : null,
                                            {
                                              shouldValidate: true,
                                            },
                                          );
                                        }
                                      }}
                                    />
                                  )}
                                />
                              </SkeletonWrapper>
                              <Typography className="validation-message">
                                {methods.formState.errors.prefix?.message}
                              </Typography>
                            </FormControl>
                          </Grid>
                        </>
                      )}
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.firstName
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            {methods.getValues("appointmentTypeCode") ===
                            "PATIENT"
                              ? "Patient First Name *"
                              : "First Name *"}
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              disabled={isPatientDetailsReadOnly}
                              size="small"
                              {...methods.register(
                                methods.getValues("appointmentTypeCode") ===
                                  "PATIENT"
                                  ? "patientFirstName"
                                  : "firstName",
                              )}
                              error={
                                methods.getValues("appointmentTypeCode") ===
                                "PATIENT"
                                  ? !!methods.formState.errors.patientFirstName
                                  : !!methods.formState.errors.firstName
                              }
                              className={`text-input ${
                                (
                                  methods.getValues("appointmentTypeCode") ===
                                  "PATIENT"
                                    ? methods.formState.errors.patientFirstName
                                    : methods.formState.errors.firstName
                                )
                                  ? "error"
                                  : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.getValues("appointmentTypeCode") ===
                          "PATIENT"
                            ? methods.formState.errors.patientFirstName?.message
                            : methods.formState.errors.firstName?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.middleName
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Middle Name
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              disabled={isPatientDetailsReadOnly}
                              size="small"
                              {...methods.register("middleName")}
                              error={!!methods.formState.errors.middleName}
                              className={`text-input ${
                                methods.formState.errors.middleName
                                  ? "error"
                                  : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.middleName?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.lastName
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            {methods.getValues("appointmentTypeCode") ===
                            "PATIENT"
                              ? "Patient Last Name *"
                              : "Last Name *"}
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              disabled={isPatientDetailsReadOnly}
                              size="small"
                              {...methods.register(
                                methods.getValues("appointmentTypeCode") ===
                                  "PATIENT"
                                  ? "patientLastName"
                                  : "lastName",
                              )}
                              error={
                                methods.getValues("appointmentTypeCode") ===
                                "PATIENT"
                                  ? !!methods.formState.errors.patientLastName
                                  : !!methods.formState.errors.lastName
                              }
                              className={`text-input ${
                                (
                                  methods.getValues("appointmentTypeCode") ===
                                  "PATIENT"
                                    ? methods.formState.errors.patientLastName
                                    : methods.formState.errors.lastName
                                )
                                  ? "error"
                                  : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.getValues("appointmentTypeCode") ===
                          "PATIENT"
                            ? methods.formState.errors.patientLastName?.message
                            : methods.formState.errors.lastName?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.preferredLanguage
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Preferred Language
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="preferredLanguage"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"preferredLanguage"}
                                  FieldName={"preferredLanguage"}
                                  options={languageData}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  isDisabled={isPatientDetailsReadOnly}
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={(e) => {
                                    if (
                                      methods
                                        ?.getValues()
                                        ?.preferredLanguage?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "preferredLanguage",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                    }
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {
                              methods.formState.errors.preferredLanguage
                                ?.message
                            }
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.maritalStatus
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Marital Status
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="maritalStatus"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"maritalStatus"}
                                  FieldName={"maritalStatus"}
                                  options={maritalStatusOptions}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  isDisabled={isPatientDetailsReadOnly}
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={(e) => {
                                    if (
                                      methods
                                        ?.getValues()
                                        ?.maritalStatus?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "maritalStatus",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                    }
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.maritalStatus?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} className={"group-container"}>
                    <Grid item container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6">
                          Contact Information
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.location
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Location / Area
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              disabled={isPatientDetailsReadOnly}
                              {...methods.register("location")}
                              error={!!methods.formState.errors.location}
                              className={`text-input ${
                                methods.formState.errors.location ? "error" : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.location?.message}
                        </Typography>
                      </Grid>
                      <Grid item sm={6}></Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.address1
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Address 1
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              disabled={isPatientDetailsReadOnly}
                              {...methods.register("address1")}
                              error={!!methods.formState.errors.address1}
                              className={`text-input ${
                                methods.formState.errors.address1 ? "error" : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.address1?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.address2
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Address 2
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              disabled={isPatientDetailsReadOnly}
                              {...methods.register("address2")}
                              error={!!methods.formState.errors.address2}
                              className={`text-input ${
                                methods.formState.errors.address2 ? "error" : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.address2?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.city
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            City
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              disabled={isPatientDetailsReadOnly}
                              {...methods.register("city")}
                              error={!!methods.formState.errors.city}
                              className={`text-input ${
                                methods.formState.errors.city ? "error" : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.city?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.postalCode
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Postal Code
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              id="postalCode"
                              size="small"
                              disabled={isPatientDetailsReadOnly}
                              {...methods.register("postalCode")}
                              error={!!methods.formState.errors.postalCode}
                              className={`text-input ${
                                methods.formState.errors.postalCode
                                  ? "error"
                                  : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.postalCode?.message}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.countryId
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Country
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="countryId"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"countryId"}
                                  FieldName={"countryId"}
                                  options={countryData}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  isDisabled={isPatientDetailsReadOnly}
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={async (e: any) => {
                                    if (
                                      methods
                                        ?.getValues()
                                        ?.countryId?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "countryId",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                      methods.setValue(
                                        "countryRegionId",
                                        null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                      if (e?.value !== null) {
                                        setIsLoading(true);
                                        await fetchCountryRegionData(e?.value);
                                        setIsLoading(false);
                                      } else {
                                        setCountryRegionData([]);
                                      }
                                    }
                                  }}
                                  className={`text-input ${
                                    methods.formState.errors.postalCode
                                      ? "error"
                                      : ""
                                  }`}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.countryId?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.countryRegionId
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            State
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="countryRegionId"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"countryRegionId"}
                                  FieldName={"countryRegionId"}
                                  options={countryRegionData}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  isDisabled={isPatientDetailsReadOnly}
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={(e) => {
                                    methods.setValue(
                                      "countryRegionId",
                                      e?.value ? e : null,
                                      {
                                        shouldValidate: true,
                                      },
                                    );
                                  }}
                                  className={`text-input ${
                                    methods.formState.errors.countryId
                                      ? "error"
                                      : ""
                                  }`}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.countryRegionId?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.email
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Email
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              disabled={isPatientDetailsReadOnly}
                              {...methods.register("email")}
                              error={!!methods.formState.errors.email}
                              className={`text-input ${
                                methods.formState.errors.email ? "error" : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.email?.message}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <div className="input-group">
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.primaryMobileNumber
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Primary Mobile Number
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <PhoneInput
                              id="primaryMobileNumber"
                              international
                              countryCallingCodeEditable={false}
                              defaultCountry="IN"
                              disabled={isPatientDetailsReadOnly}
                              {...methods.register("primaryMobileNumber")}
                              error={
                                methods.formState.errors.primaryMobileNumber
                                  ?.message
                              }
                              className={`phone-input ${
                                methods.formState.errors.primaryMobileNumber
                                  ? "error"
                                  : ""
                              }`}
                              onChange={(e) => {
                                methods.setValue(
                                  "primaryMobileNumber",
                                  e || "",
                                );
                              }}
                              value={methods.getValues()?.primaryMobileNumber}
                              placeholder="Enter phone number"
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {
                              methods.formState.errors.primaryMobileNumber
                                ?.message
                            }
                          </Typography>
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <div className="input-group">
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.secondaryMobileNumber
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Secondary Mobile Number
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <PhoneInput
                              id="secondaryMobileNumber"
                              international
                              countryCallingCodeEditable={false}
                              defaultCountry="IN"
                              disabled={isPatientDetailsReadOnly}
                              {...methods.register("secondaryMobileNumber")}
                              error={
                                methods.formState.errors.secondaryMobileNumber
                                  ?.message
                              }
                              className={`phone-input ${
                                methods.formState.errors.secondaryMobileNumber
                                  ? "error"
                                  : ""
                              }`}
                              value={methods.getValues()?.secondaryMobileNumber}
                              onChange={(e) => {
                                methods.setValue(
                                  "secondaryMobileNumber",
                                  e || "",
                                );
                              }}
                              placeholder="Enter phone number"
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {
                              methods.formState.errors.secondaryMobileNumber
                                ?.message
                            }
                          </Typography>
                        </div>
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              )}

              {/* {!isCalendar && ( */}
              <Grid item xs={12} mt={1}>
                <Box
                  sx={{
                    display: "flex",
                    // justifyContent: "end",
                    justifyContent: "space-between",
                    alignItems: "center",
                    // gap: 2,
                  }}
                >
                  <Box>
                    {isCalendar &&
                      curAppointmentId > 0 &&
                      methods.getValues()?.status === "SCHEDULED" && (
                        <LoadingButton
                          color="error"
                          disabled={isLoading || methods.formState.isSubmitting}
                          loadingPosition="start"
                          startIcon={<></>}
                          variant="outlined"
                          type="button"
                          className="secondary-button"
                          name="cancel"
                          onClick={() => {
                            handleDeleteDilog(true);
                          }}
                        >
                          Cancel Appointment
                        </LoadingButton>
                      )}
                  </Box>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    {" "}
                    {!["COMPLETE", "CANCELLED"].includes(
                      methods.getValues("status"),
                    ) && (
                      <LoadingButton
                        color="primary"
                        loadingPosition="start"
                        startIcon={<></>}
                        variant="contained"
                        type="submit"
                        className="primary-button"
                        name="save"
                        disabled={isLoading || methods.formState.isSubmitting}
                      >
                        {curAppointmentId > 0 ? "Update" : "Save"}
                      </LoadingButton>
                    )}
                    {isCalendar ? (
                      <LoadingButton
                        color="primary"
                        disabled={isLoading || methods.formState.isSubmitting}
                        loadingPosition="start"
                        startIcon={<></>}
                        variant="outlined"
                        type="button"
                        className="secondary-button"
                        onClick={handleBack}
                      >
                        Close
                      </LoadingButton>
                    ) : (
                      <LoadingButton
                        color="primary"
                        disabled={isLoading || methods.formState.isSubmitting}
                        loadingPosition="start"
                        startIcon={<></>}
                        variant="outlined"
                        type="button"
                        className="secondary-button"
                        onClick={handleBack}
                      >
                        Cancel
                      </LoadingButton>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>

        {/* PATIENT */}
        <Dialog
          className="dialogPage"
          open={
            customPopUp.open &&
            (customPopUp.code === "PATIENT" ||
              customPopUp.code === "REFERRAL_PATIENT")
          }
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "1200px",
              height: "700px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                {customPopUp.code === "PATIENT"
                  ? "Save Patient"
                  : "Save Referral Patient"}
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <PatientSave
              handleClosePopUp={(code: string, company: any) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={true}
              name={customPopUp.name}
              code={customPopUp.code}
              opd={{ patientId: methods.getValues("patientId")?.value || 0 }}
            />
          </DialogContent>
          <Divider />
        </Dialog>
        {/* MEDICAL_REPRESENTATIVE */}
        <Dialog
          className="dialogPage"
          open={
            customPopUp.open && customPopUp.code === "MEDICAL_REPRESENTATIVE"
          }
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "1200px",
              height: "700px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Save Medical Representative
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <MedicalRepresentativeSave
              handleClosePopUp={(code, company) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={true}
              name={customPopUp.name}
            />
          </DialogContent>
          <Divider />
        </Dialog>
        {/* ITEM */}
        <Dialog
          className="dialogPage"
          open={customPopUp.open && customPopUp.code === "ITEM"}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "1200px",
              height: "700px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Save Reason For Visit
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <ItemSave
              handleClosePopUp={(code, company) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={true}
              name={customPopUp.name}
              code={customPopUp.code}
            />
          </DialogContent>
          <Divider />
        </Dialog>
        {/* REFERRAL_DOCTOR */}
        <Dialog
          className="dialogPage"
          open={customPopUp.open && customPopUp.code === "REFERRAL_DOCTOR"}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "1200px",
              height: "700px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Save Referral Doctor
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <DoctorSave
              handleClosePopUp={(code: any, company: any) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={true}
              name={customPopUp.name}
              code={customPopUp.code}
            />
          </DialogContent>
          <Divider />
        </Dialog>

        {/*  CALENDAR CANCEL */}
        <Dialog
          open={openDeleteDilog}
          onClose={() => {
            handleDeleteDilog(false);
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to cancel this Appointment?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                deleteRow(curAppointmentId);
              }}
              className="button-delete"
            >
              Yes
            </Button>
            <Button
              onClick={(e) => {
                handleDeleteDilog(false);
              }}
            >
              No
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
